'use client';

/**
 * **The inventory record as attribute blocks — the seam every conversion ticket
 * plugs into** (VALE-75 / [INVDRAW-4]).
 *
 * `[INVDRAW-4]` shipped the container and this seam; it deliberately did **not**
 * ship per-type specs. `[INVDRAW-5]` (VALE-76) adds the first real one —
 * `bank-account`, ported off `components/prototypes/inventory-drawer/`'s spec
 * builder — and `[INVDRAW-6…13]` follow with the remaining 21 types. Any type not
 * yet registered in {@link INVENTORY_BLOCK_BUILDERS} still renders
 * {@link baselineBlocks}, which is the small set of fields all 22 genuinely
 * share.
 *
 * ## The contract a per-type builder has to satisfy
 *
 * One function, `(context) => AttributeBlock<UpdateInventoryItemRequest>[]`. The
 * block union is `[INVDRAW-2]`'s ({@link AttributeBlock}) — `rows` with an
 * optional `title: null` continuation and per-block `columns`, a full-width
 * `component` that owns its own layout, or a bare `heading`. Register it in
 * {@link INVENTORY_BLOCK_BUILDERS} keyed by type slug; an unregistered slug falls
 * back to the baseline, so a half-converted rollout never renders an empty
 * drawer.
 *
 * ## The three traps a spec here must respect
 *
 * All three are properties of the write contract, verified against `develop`, and
 * all three are invisible to typecheck:
 *
 * 1. **`itemInfo: null` wipes the entire blob.** The service branches on it
 *    explicitly, so an `info`-backed field must set `clearTo: ''` — never `null`.
 *    {@link infoPatch} is the only sanctioned way to build an `itemInfo` patch
 *    for exactly this reason.
 * 2. **LF-4356's clear-on-edit invariant.** Changing a value figure **nulls the
 *    confirmation that vouched for it**. Under commit-on-blur that fires per
 *    field-commit rather than once per Save — same rule, more often. A spec does
 *    not implement it (the backend does); it must not work around it.
 * 3. **A source is what confirms a value** — see the header note on the
 *    confirmation gate in `docs/attribute-treatment.md` §2.1. There is no
 *    separate "confirmed" flag for a spec to set.
 */

import { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  ATTRIBUTE_LABEL_TEXT,
  ATTRIBUTE_MUTED_INK,
  bankAccountCopyText,
  formatBankAccountSummary
} from '@/components/attributes';
import { shareOwnershipChange } from '@/lib/services/inventory-items/share-ownership';
import { vehicleInstructionChange } from '@/lib/services/inventory-items/vehicle-instruction';
import { cn } from '@/lib/utils';
import { Banner } from '@/components/ui/banner';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { confirmationProvenanceTip } from '@/lib/services/inventory-items/confirmation-provenance';
import {
  EstimatedFigure,
  isEstimatedFigure
} from '@/lib/services/inventory-items/estimate-figure';
import type {
  AttributeBankAccount,
  AttributeBlock,
  AttributeDraft,
  AttributeEditor,
  AttributeMarketFigure,
  AttributeOption,
  AttributeSpec,
  AttributeValueConfirmation
} from '@/components/attributes';
import {
  inventoryStatusLabel,
  inventoryStatusOptions,
  isResolvableAssetType,
  isSettledLiabilityLocked
} from '@/lib/services/inventory-items/status-options';
import {
  LIABILITY_TYPE_SLUGS,
  SHARE_OWNERSHIP_LABELS,
  type InventoryItem,
  type ShareInstruction,
  type ShareOwnershipType,
  type ShareTransferBeneficiary,
  type UpdateInventoryItemRequest
} from '@/lib/services/inventory-items/types';
import {
  formatCurrency,
  roundCents
} from '@/lib/formatters/australian-formatters';
import {
  InstitutionSearchField,
  type CustomInstitution,
  type Institution
} from '@/components/inventory/inventory-form-card/shared/InstitutionSearchField';
import { describeChangeValue } from '@/components/ai-review/change-status';
import { computeBankAccountDodValue } from '@/lib/utils/bank-account-value';
import {
  computeNursingHomeBondTotal,
  isNursingHomeBondTotalReconciled,
  type NursingHomeBondComponents
} from '@/lib/utils/nursing-home-bond';
import { VehicleInstructionSection } from '@/components/inventory/inventory-form-card/shared/VehicleInstructionSection';
import type { VehicleTransferBeneficiaryDraft } from '@/components/inventory/inventory-form-card/shared/vehicle-instruction/constants';
import {
  OWNERSHIP_TYPE_OPTIONS,
  formatSelectLabel,
  REPAYMENT_FREQUENCY_OPTIONS,
  ENCUMBRANCE_TYPE_OPTIONS,
  RESOLUTION_PLAN_OPTIONS
} from '@/components/inventory/inventory-form-card/utils';
import { TAX_TYPE_OPTIONS } from '@/components/inventory/inventory-form-card/item-types/TaxLiabilityForm';
import { AUSTRALIAN_STATES } from '@/lib/constants/australian-constants';
import { VERBATIM_INSTITUTION_NAME_FIELDS } from '@/lib/constants/verbatim-institution-name-fields';
import { PRIVATE_COMPANY_OWNERSHIP_OPTIONS } from '@/components/inventory/inventory-form-card/item-types/PrivateCompanyForm';
import type {
  OtherInstruction,
  VehicleInstruction
} from '@/lib/services/redemption';
import {
  OtherInstructionKept,
  SavedOtherInstructionKept
} from './other-instruction-kept';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import {
  accountNumberSchema,
  AU_STATE_CODES,
  bsbSchema,
  hinSchema,
  isAustralianAddress,
  srnSchema
} from '@/lib/schemas/inventory-form-schemas';
import {
  getTitleReferenceLabel,
  validateTitleReference
} from '@/lib/constants/title-reference';
import { ESTATE_TYPES } from '@/components/inventory/inventory-form-card/item-types/RealEstateForm';
import { PropertyInstructionSection } from '@/components/inventory/inventory-form-card/shared/PropertyInstructionSection';
import type { TransferBeneficiary } from '@/components/inventory/inventory-form-card/shared/property-instruction/constants';
import type {
  BeneficiaryHoldingType,
  PropertyInstruction,
  PropertyTransferSubFlow
} from '@/lib/services/redemption';
import type { PostalAddress } from '@/lib/services/matters/types';
import type { Contact } from '@/lib/services/contacts/types';
import {
  SrnShareSearchField,
  type CustomSrnShare
} from '@/components/inventory/inventory-form-card/shared/SrnShareSearchField';
import {
  ShareInstructionSection,
  type ShareInstructionChanges
} from '@/components/inventory/inventory-form-card/shared/ShareInstructionSection';
import type { SrnShare } from '@/lib/services/srn-shares/hooks';
import type { ItemBeneficiaryShare } from '@/lib/services/entitlements/specific-gift-defaults';
import {
  HinShareSearchField,
  type CustomHinShare
} from '@/components/inventory/inventory-form-card/shared/HinShareSearchField';
import type { TickerSearchResult } from '@/lib/services/stocks/types';
import type { SharePriceSync } from './share-price-sync';
import {
  pricePatchKeyPaths,
  recordedSharePriceSync,
  shareScopeFor
} from './share-price-sync';
import {
  SHARE_EXCHANGE,
  getFreshness
} from '@/components/inventory/inventory-form-card/shared/share-price-freshness';
import {
  CryptoPortfolioKept,
  type CryptoHolding
} from './crypto-portfolio-kept';

/** The patch every row on this surface produces. */
export type InventoryPatch = UpdateInventoryItemRequest;

export interface InventoryBlockContext {
  item: InventoryItem;
  /** The item's type slug, already resolved off the string-or-object union. */
  typeSlug: string;
  /**
   * **Whether this item runs the administration lifecycle** — notification of
   * death, the instruction workflows, realisation and settlement — or is a
   * static register entry (VALE-1764).
   *
   * Estate planning is where the two diverge: a planning matter holds real
   * `InventoryItem` rows so a gift can name a real thing, and none of them is
   * chased, instructed, realised or settled. The backend refuses every one of
   * those writes (`matterTypeSupportsInventoryLifecycle`,
   * `assertInventoryStatusAllowedOnMatterType`), so what this governs is whether
   * the drawer *offers* them — a control that 400s is worse than no control.
   *
   * **Defaults to `true`**, so every existing caller is byte-identical: the
   * surfaces that mount this drawer today are all estate-administration ones,
   * and the review surfaces render over items that will become administration
   * rows.
   */
  runsLifecycle?: boolean;
  /**
   * What a per-type builder needs to render the **institution search kept
   * component** (`components/attributes/CLAUDE.md` §7b — a `component` block is
   * a plain `React.ReactNode` the builder constructs itself, so this is the data
   * it constructs one from). Optional: only a type whose builder actually keeps
   * an institution search reads these; `baselineBlocks` never does.
   */
  institutions?: Institution[];
  loadingInstitutions?: boolean;
  onInstitutionSearch?: (query: string) => void;
  /**
   * **What a value's confirmation can cite** (VALE-355) — the matter's documents
   * plus, grouped separately, the attachments on the item institution's own
   * replies (LF-5393). Read by {@link confirmationRow}'s `valueSource` editor.
   *
   * Supplied by the surface rather than fetched by the spec for the same reason
   * `institutions` is: a builder is a pure function of its context, and a fetch
   * inside one would run once per row per render.
   */
  valueSourceDocuments?: AttributeOption[];
  /**
   * What the document picker says when {@link valueSourceDocuments} is empty —
   * still loading, the fetch failed, or this matter genuinely has no documents.
   * Only the surface holding the query can tell those apart, and a picker that
   * renders "no documents" over a failed fetch is the defect LF-6314 fixed on the
   * flag-off form's own placeholder.
   */
  valueSourceDocumentsPlaceholder?: string;
  onInstitutionChange?: (
    institution: Institution | CustomInstitution | string | null
  ) => void;
  /**
   * Whether the institution/facility field currently accepts a change (LF-5319
   * — the backend blocks it once money is tied to the asset). Also what gates
   * `Estate reference` visible: that field only matters once the facility is
   * locked in, exactly as the flag-off form shows it only then.
   */
  isInstitutionEditable?: boolean;
  /**
   * Matter contacts, for a reference-select or a kept beneficiary picker over
   * people — `[INVDRAW-9]`'s real-estate transfer-to-beneficiary allocation
   * panel, `vehicle-loan`'s Payee row ([INVDRAW-10] / VALE-81),
   * `VehicleInstructionSection`'s kept beneficiary picker, the kept
   * `ShareInstructionSection` (mirrors `InventoryFormCard`'s own `isShareType`
   * gate), and, since `[INVDRAW-7]` / VALE-78, every liability's
   * {@link payeeRows} (mortgage's Payee row among them). Optional: only a
   * builder whose type actually needs a person picker reads these.
   */
  contacts?: Contact[];
  loadingContacts?: boolean;
  /**
   * Sibling inventory items a liability can link to — `mortgage` →
   * `real-estate` (`[INVDRAW-9]`), `vehicle-loan` → `vehicle` ([INVDRAW-10] /
   * VALE-81) — `LinkedAssetField`'s equivalent as a reference select.
   * Filtered from the already-loaded inventory cache by the drawer; no extra
   * fetch.
   */
  linkedAssets?: Array<{
    id: number;
    description: string;
    value: number | null;
  }>;
  /**
   * **A kept component's own change, already resolved to a patch** —
   * `[INVDRAW-9]`'s property-instruction block (a cascading reset the
   * block's own `toPatch` can't express), [INVDRAW-10] / VALE-81's
   * `VehicleInstructionSection` (up to three `itemInfo` keys in one
   * gesture), and [INVDRAW-12] / VALE-83's generalisation of the pattern
   * `onInstitutionChange` established. Institution search needed
   * backend-specific resolution (`resolveInstitutionUpdateFields`) that has
   * to live in the drawer; every other kept component resolves its own patch
   * shape inline in the spec, so a single generic escape hatch is enough —
   * see `crypto-portfolio-kept.tsx`. The share types reach the same hatch
   * without a kept component at all: `share-price-sync.ts` hands it to the
   * fetch, and {@link sharePriceBlocks}' rows commit through it (VALE-1008).
   * Optional: only a builder that mounts one of those kept components reads
   * it.
   */
  onCommit?: (patch: InventoryPatch) => void;
  /**
   * **The matter the record belongs to** (VALE-1940), for the one block that
   * saves through an endpoint of its own: an "other" asset's instruction, whose
   * answer moves the item's status, clears a realised value it no longer applies
   * to and requires a recipient for a transfer (`OtherInstructionService`), all
   * of which a plain `itemInfo` write would skip. Absent, or `0` where there is
   * no matter yet.
   */
  matterId?: number;
  /**
   * **There is no server behind this record** — it is a local buffer of request
   * patches, so anything the API would DERIVE has to be read off the intent
   * instead.
   *
   * Three surfaces are in it, and the name says the property they share rather
   * than the one that first needed it. The add dialog's buffered surface
   * ([INVDRAW-17] / VALE-98), where a commit lands in local state and a single
   * `POST` on Save creates the item; Discovery's asset add card; and — added
   * under VALE-1617 — **the two AI review surfaces**, whose extracted items are
   * unsaved JSON held in the flow (`PendingInventorySource`).
   *
   * **It was called `addMode` and that name cost a shipped bug.** The review
   * items are every bit as bufferless as an add dialog's draft, but they are
   * not *adds*, so nobody thought to pass the flag: `figureConfirmed` went on
   * reading the API-derived `currentValueConfirmed`, which a buffer never
   * holds, while the commit wrote `currentValueConfirmation`. Answering
   * `Confirmed against a source → Yes` in review rendered straight back to
   * `No`, indistinguishable from a read-only control. The drawer derives this
   * from `useInventoryRecordSource`'s own `isPending` now, so a third buffered
   * surface cannot forget it.
   *
   * Every builder is otherwise unchanged, which is this ticket's whole
   * economy: the same 22 recipes render, the same `toPatch` shapes are
   * produced, and only *where the patch goes* differs.
   *
   * **A create *can* now carry a value confirmation** (VALE-355) — it could not
   * until `CreateInventoryItemDto` was widened, which is why
   * {@link confirmationRow} used to hide itself here. What a builder still has to
   * know is that in add mode the buffer holds the *request* shape (the intent),
   * not the API's derived `dodValueConfirmed`, so a row reading confirmation
   * state must branch on this flag — see {@link confirmationRow}'s header.
   *
   * The flag-off add dialog still never passes `InventoryFormCard`'s
   * `showValueConfirmation` (whose own doc comment requires a persisted
   * `item.id`), so provenance-at-add is a flagged-path capability only.
   *
   * `Status` needs no flag: it is drawer *chrome* rendered through
   * `AttributeChromeValue` ({@link statusRow}), never a block, so a surface
   * opts in by rendering it. The add dialog does not — matching the flag-off
   * path's `showStatusDropdown={false}`.
   */
  bufferedRecord?: boolean;
  /**
   * The matter's decedent date of death, `YYYY-MM-DD` — needed by the share
   * types' date-of-death price fetch. Not threaded until `[INVDRAW-12]`, the
   * first type that needed it ([INVDRAW-4]'s Actuals flagged this as deferred
   * until then).
   */
  dateOfDeath?: string;
  /**
   * **The share types' market figures and the fetch behind them** (VALE-1008) —
   * read by {@link sharePriceBlocks}.
   *
   * Supplied by the surface rather than fetched by the spec, for the reason
   * {@link InventoryBlockContext.valueSourceDocuments} gives: a builder is a pure
   * function of its context, and a fetch inside one would run once per row per
   * render. `useSharePriceSync` is the hook a host calls to produce it.
   *
   * Absent on every non-share type, and absent on a share type whose host has not
   * threaded it — in which case the price rows simply do not render, the same way
   * a builder that gets no `institutions` renders no picker options.
   */
  sharePrice?: SharePriceSync | null;
  /**
   * Per-contact specific-gift entitlement shares for this holding (LF-4121),
   * used to seed the transfer-beneficiary allocation the first time
   * "Transfer to Beneficiary" is selected. Mirrors `InventoryFormCard`'s own
   * `entitlementBeneficiaryShares` resolution.
   */
  entitlementBeneficiaryShares?: ItemBeneficiaryShare[];
  /**
   * Whether the fetches {@link entitlementBeneficiaryShares} and {@link contacts}
   * come from are still in flight (VALE-817). Selecting the instruction before
   * they land used to seed nothing, permanently — the seed is a one-shot on that
   * transition, so reopening the drawer never retried it. `ShareInstructionSection`
   * defers the seed while this is set.
   */
  entitlementSeedLoading?: boolean;
  /**
   * **The payee checkbox's pending choice, when it disagrees with the
   * record** ([INVDRAW-7] / VALE-78, see {@link payeeRows}). The record
   * stores only `payeeContactId`, so "ticked, no contact chosen yet" has
   * nowhere else to live. `undefined` (the usual case) means "read it off
   * `payeeContactId`".
   */
  pendingPaidOutOfPocket?: boolean;
  onPaidOutOfPocketChange?: (next: boolean) => void;
}

/**
 * An `itemInfo` patch for one key.
 *
 * **The only sanctioned way to write `itemInfo` from a spec**, because it is what
 * keeps trap 1 above impossible to hit by accident: the backend merges
 * `{ ...existing, ...incoming }` for an object and **wipes the whole blob** for
 * `null`, so a row that cleared to `null` would erase every other field on the
 * record. A cleared `info` field sends `''`, which the backend's own
 * `emptyStringToNull` turns into a cleared value.
 */
/**
 * **The bank-account row's draft, from the item's `info` blob** (VALE-1211).
 *
 * `null` when neither number is captured, so an empty account renders the row's
 * `Set bank account…` placeholder rather than an object of empty strings — the
 * same rule the editor's own `normalise` applies.
 *
 * The account name is its own row, first on the record (VALE-1940), and this
 * item type stores no verification, so the shape carries only the two numbers
 * and the spec offers neither part.
 */
function bankAccountDraft(item: InventoryItem): AttributeBankAccount | null {
  const bsb = infoString(item, 'bsb');
  const accountNumber = infoString(item, 'accountNumber');
  return bsb || accountNumber ? { bsb, accountNumber } : null;
}

export function infoPatch(
  key: string,
  value: string | number | boolean | null
): InventoryPatch {
  return { itemInfo: { [key]: value ?? '' } };
}

/**
 * **One patch for a multi-key share-instruction change** (VALE-857).
 *
 * `ShareInstructionSection` reports "the instruction moved, and so did the
 * allocation/ownership it forces" as a single change, because on this path every
 * commit is its own `PUT` and two in the same tick raced in the backend's
 * `itemInfo` merge — whichever landed last erased the other's key, silently
 * dropping the allocation a lawyer had just watched auto-fill.
 *
 * Built on {@link infoPatch} rather than restating its empty-string contract:
 * `instruction` is an `info` field like any other and must clear to `''`.
 */
function shareFieldsPatch(changes: ShareInstructionChanges): InventoryPatch {
  const itemInfo: Record<string, unknown> = {};
  if (changes.instruction !== undefined) {
    Object.assign(
      itemInfo,
      infoPatch('instruction', changes.instruction).itemInfo
    );
  }
  if (changes.ownershipType !== undefined) {
    Object.assign(
      itemInfo,
      infoPatch('ownershipType', changes.ownershipType).itemInfo
    );
  }
  if (changes.transferBeneficiaries !== undefined) {
    itemInfo.transferBeneficiaries = changes.transferBeneficiaries;
  }
  return {
    // Omitted rather than sent empty: `itemInfo: {}` is a harmless merge, but it
    // still routes the request through the backend's share-instruction enrich
    // and event path for a request that changes no `info` field at all.
    ...(Object.keys(itemInfo).length > 0 ? { itemInfo } : {}),
    ...(changes.isNonEstate !== undefined
      ? { isNonEstate: changes.isNonEstate }
      : {})
  };
}

const infoString = (item: InventoryItem, key: string): string => {
  const info = item.itemInfo as Record<string, unknown> | null;
  const value = info?.[key];
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
};

const infoNumber = (item: InventoryItem, key: string): number | null => {
  const info = item.itemInfo as Record<string, unknown> | null;
  const value = info?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * **A plain free-text `info` field as a row** (VALE-1940): the label, the key,
 * and the empty-string clear `infoPatch` owns. For the fields the AI may propose
 * that had no row, so a reviewer can correct them where they read them.
 */
function infoTextRow(
  item: InventoryItem,
  label: string,
  key: string,
  extra: Partial<AttributeSpec<InventoryPatch>> = {}
): AttributeSpec<InventoryPatch> {
  const value = infoString(item, key);
  return {
    label,
    display: value || null,
    draft: value,
    editor: { kind: 'text' },
    toPatch: (draft) =>
      typeof draft === 'string' ? infoPatch(key, draft) : null,
    ...extra
  };
}

/**
 * **A liability's own description** (VALE-1940). Free text the AI proposes and
 * the Distribution planner prints as the liability's subtitle; distinct from the
 * composed name on the row line, which is `formatLineItemDisplay`'s.
 */
function liabilityDescriptionRow(
  item: InventoryItem
): AttributeSpec<InventoryPatch> {
  return descriptionRow(item);
}

/**
 * **An item's free-text description, in the shape of `Notes`** (VALE-1940):
 * multi-line, the full width, clearable. It is prose like Notes, so it reads and
 * edits like Notes wherever it appears (the other asset, employment
 * entitlements, every liability).
 */
function descriptionRow(item: InventoryItem): AttributeSpec<InventoryPatch> {
  return infoTextRow(item, 'Description', 'description', {
    multiline: true,
    fullWidth: true,
    clearTo: ''
  });
}

/**
 * **A loan's account number** (VALE-1940), printed on the amount-owed letter
 * (`amount-owed.html`), so a reviewer must be able to correct what the AI read.
 */
function loanAccountNumberRow(
  item: InventoryItem
): AttributeSpec<InventoryPatch> {
  const value = infoString(item, 'accountNumber');
  return infoTextRow(item, 'Account number', 'accountNumber', {
    copyable: true,
    copyText: value
  });
}

/**
 * The top-level `isNonEstate` boolean reads "Non-estate asset" on an asset and
 * "Non-estate liability" on a liability (`InventoryFormCard.tsx`'s own generic
 * checkbox — the label the flag-off form has shown for every liability type
 * since before this rollout). `bankAccountBlocks` and `nursingHomeBondBlocks`
 * never needed this: both are assets, so the hardcoded "asset" wording never
 * had a liability to disagree with. `vehicle-loan` ([INVDRAW-10] / VALE-81) is
 * the first liability converted, and `baselineBlocks` is the fallback every
 * *unconverted* liability renders today — both need the real label.
 *
 * **Exported since [INVDRAW-8] / VALE-79** — that ticket converts the last
 * four of the 10 liability slugs, so `baselineBlocks` (the only production
 * caller) is no longer reachable with any real liability typeSlug. The test
 * suite now exercises this function directly rather than through the
 * now-unreachable fallback path — see the "Non-estate label" describe block
 * in `inventory-attribute-specs.test.ts`.
 */
export function nonEstateLabel(typeSlug: string): string {
  return LIABILITY_TYPE_SLUGS.has(typeSlug)
    ? 'Non-estate liability'
    : 'Non-estate asset';
}

/**
 * **`Status` — whole-record chrome, not a row** (`[INVDRAW-4]`'s review
 * decision).
 *
 * It renders in the drawer header through {@link AttributeChromeValue}, so it is
 * exported on its own rather than appearing in any block: production already
 * shows a status badge beside the title on all four families, and as the last row
 * of the grid the most-scanned fact on the drawer was the thing a reader reached
 * last. The header carries it *instead of* a badge — a badge as well would state
 * the same fact twice.
 *
 * The vocabulary, the labels and the two backend-refusal rules that shape them
 * come from `status-options.ts`, which the flag-off path's own dropdown also
 * binds to.
 */
/**
 * The statuses a **register-only** matter type may hold, mirroring the backend's
 * `REGISTER_ONLY_INVENTORY_STATUSES`: recorded, checked, and no longer part of
 * the plan. `archived` is the register's delete-free exit — a planning gift's
 * subject IS its item, so deleting one would leave a clause saying a beneficiary
 * receives *something*.
 */
function registerStatusOptions(
  currentStatus: string
): { value: string; label: string; disabled?: boolean }[] {
  const register = ['draft', 'confirmed', 'archived'];
  const values = register.includes(currentStatus)
    ? register
    : [...register, currentStatus];
  return values.map((value) => ({
    value,
    label: inventoryStatusLabel(value as InventoryItem['status'])
  }));
}

export function statusRow(
  context: InventoryBlockContext
): AttributeSpec<InventoryPatch> {
  const { item, typeSlug, runsLifecycle = true } = context;
  const currentStatus = item.status ?? 'draft';
  const locked = isSettledLiabilityLocked({
    category: item.category,
    currentStatus
  });

  return {
    label: 'Status',
    display: inventoryStatusLabel(currentStatus),
    draft: currentStatus,
    editor: {
      kind: 'select',
      options: (runsLifecycle
        ? inventoryStatusOptions({
            category: item.category,
            resolvableAsset: isResolvableAssetType(typeSlug),
            currentStatus
          })
        : /*
            A register keeps three statuses and the API refuses the rest
            (`REGISTER_ONLY_INVENTORY_STATUSES`). The seven excluded ones all
            describe chasing a DECEASED person's asset through an institution —
            notifying the bank, learning whether it holds the account,
            collecting the money — none of which can happen for a living client.
            Offering them here would be offering a commit that 400s.

            The current status is kept in the list whatever it is, so a row that
            somehow holds an administration status can still be read back and
            moved off it rather than showing a picker that cannot express it.
          */
          registerStatusOptions(currentStatus)
      )
        /*
          A disabled option cannot be expressed as an `AttributeOption`, and it
          exists only to show the reader the state they are in — which the
          read-out already does. So it is dropped from the choices, and `locked`
          below refuses the commit with the reason instead.
        */
        .filter((option) => !option.disabled)
        .map(({ value, label }) => ({ value, label }))
    },
    /*
      A closed vocabulary with no absent member: every item has a status, and
      `draft` is how it says "not started". Clearing would return the row to a
      state its own picker cannot re-select.
    */
    required: true,
    validate: (draft) =>
      locked
        ? 'A settled liability’s status is set by its distribution. Reverse the distribution to change it.'
        : typeof draft === 'string' && draft.length > 0
          ? null
          : 'Choose a status.',
    toPatch: (draft) =>
      typeof draft === 'string' && draft.length > 0
        ? { status: draft as InventoryItem['status'] }
        : null
  };
}

/**
 * **Money, as an attribute row** (`components/attributes/CLAUDE.md` §3,
 * [INVDRAW-1] — shipped). Ported from
 * `components/prototypes/inventory-drawer/inventory-attribute-specs.tsx`'s
 * settled `money()` builder, the design source this recipe ports verbatim
 * rather than re-deriving.
 *
 * `signed` carries the asset/liability split: a liability's balance can be a
 * *credit* (the institution owes the estate), an asset's cannot. Bank account
 * is unsigned throughout — it has no credit concept.
 */
/**
 * **A kept picker as one row of the grid** (VALE-997) — the single shape all 20
 * institution and share searches take.
 *
 * VALE-863 moved these controls out of `component` blocks and into the
 * two-column field flow, and each of the 18 call sites that made the move spelled
 * the same six members out by hand. That is the restatement root `CLAUDE.md`
 * warns about: the shape was a fact the file owned in twenty places, so the
 * twenty drifted — the two that stayed `component` blocks (`nursing-home-bond`'s
 * `Facility`, `mortgage`'s `Lender`) kept a one-column cap the other sixteen had
 * lost, and adding `fullWidth` to sixteen literals would only have widened the
 * gap. Stated once here, "the picker rows read as one pattern" is structural
 * rather than a thing each new call site has to remember.
 *
 * **`fullWidth` is the fix VALE-997 is for.** As half-width cells these rows
 * shared a grid line with the field beside them, so a registry name that wrapped
 * to two lines — `MUFG Corporate Markets (AU) Limited` — set the line's height
 * and left `Ticker` beside it off its own baseline. A whole line lets the
 * picker's height vary without anything else moving, and it is the width the
 * provenance caption (`Email: …`) needs to sit on its own line beneath the name.
 *
 * The row keeps everything else the grid gives it: its label in the shared label
 * column, its place in the field order, and the motion it has when it appears or
 * leaves. `readOnly` + a `null` `toPatch` are what say the *grid* has nothing to
 * commit here — see {@link AttributeSpec.render}: the control owns its own write,
 * often several keys at once.
 */
/**
 * A kept picker as one row, plus the diff key it stands for (VALE-1181).
 *
 * `diffKey` is now an `AttributeSpec` field (VALE-1181) rather than inventory's
 * own extension — a row whose patch is not a pure function of its draft has to
 * be able to declare its key, and that is a property of any attribute row, not
 * of an inventory one. The grid still neither reads nor needs it; only a surface
 * diffing the record asks. This alias survives because a picker row is
 * additionally guaranteed to HAVE one, which the optional field cannot say.
 */
export type InventoryPickerSpec = AttributeSpec<InventoryPatch> & {
  /** The key the control draws first, then any other key its commit writes. */
  diffKey: string | readonly string[];
};

/** The key a picker's control DRAWS: its only key, or the first it declares. */
function drawnPickerKey(diffKey: string | readonly string[]): string {
  return typeof diffKey === 'string' ? diffKey : diffKey[0];
}

function pickerRow(
  label: string,
  render: () => React.ReactNode,
  /**
   * **The diff key this picker's value is carried under** (VALE-1181).
   *
   * Stated because it cannot be derived: `readOnly` + a `null` `toPatch` are how
   * a kept control says *the grid has nothing to commit here*, so the row
   * legitimately produces no contract keys — and anything reading keys off the
   * patch therefore cannot tell what the row is about. The AI review needs to:
   * a changed institution has to mark the row that NAMES the institution, and on
   * a created item that row is the only place the extracted name appears at all.
   *
   * `institution` is the default because 18 of the 20 pickers are an institution
   * search under one label or another (Bank, Fund, Lender, Card Issuer, Share
   * Registry…), all bound to the same `item.institution` through the same
   * `onInstitutionChange`. The two exceptions are the share-company searches,
   * which write `companyName` / `ticker` / `institutionId` together and whose
   * value a reader knows by its ticker.
   */
  diffKey: string | readonly string[] = 'institution'
): InventoryPickerSpec {
  return {
    label,
    display: null,
    draft: '',
    editor: { kind: 'text' },
    readOnly: true,
    toPatch: () => null,
    fullWidth: true,
    render,
    diffKey
  };
}

/**
 * Parses a money editor's raw draft: an empty string clears (`null`), a valid
 * figure is itself, anything else is invalid (`undefined`) — shared by
 * {@link moneyRow} and {@link derivingMoneyRow} so the two parse a draft
 * identically rather than drifting.
 */
function parseMoneyDraft(draft: unknown): number | null | undefined {
  const raw = typeof draft === 'string' ? draft.trim() : '';
  if (raw.length === 0) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * A raw component draft as a number, or `undefined` when it is empty or mid-edit
 * (VALE-967). Feeds a `deriveAmount`, whose helpers take numbers: an absent part
 * must read as absent rather than as a zero the reader never entered, which is what
 * keeps a blank breakdown showing no figure instead of `$0.00`.
 */
function toNumberOrUndefined(raw: string | undefined): number | undefined {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function moneyRow(
  label: string,
  key: string,
  item: InventoryItem,
  { signed = false }: { signed?: boolean } = {}
): AttributeSpec<InventoryPatch> {
  const value = infoNumber(item, key);
  /*
    [INVDRAW-7] / VALE-78: a negative *liability* balance is a credit — the
    institution owes the estate, not the reverse. `ValuePairFields`
    (`inventory-form-card/shared/ValuePairFields.tsx`) says so via its
    `negativeIsCredit` caption; there is no equivalent hint slot on
    `AttributeSpec`, so this appends the same fact to the plain-text
    `display` rather than leaving a signed negative figure looking like an
    unexplained oddity. Inert for `signed: false` (every caller before this
    ticket), so no shipped consumer's read-out changes.
  */
  const isCredit = signed && value != null && value < 0;
  return {
    label,
    display:
      value == null
        ? null
        : isCredit
          ? `${formatCurrency(value)} · recorded as a credit`
          : formatCurrency(value),
    draft: value == null ? '' : String(value),
    editor: { kind: 'money', signed },
    copyText: value == null ? '' : formatCurrency(value),
    toPatch: (draft) => {
      const parsed = parseMoneyDraft(draft);
      return parsed === undefined ? null : infoPatch(key, parsed);
    },
    validate: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (raw.length === 0) return null;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return `${label} must be an amount.`;
      if (!signed && parsed < 0) {
        return `${label} cannot be negative. Only a liability balance can be a credit.`;
      }
      return null;
    }
  };
}

/**
 * **A money row that is also a component of a derived total** ([INVDRAW-11] /
 * VALE-82 — the derive-but-don't-clobber rule).
 *
 * Identical to {@link moneyRow} in every respect a reader can see or a
 * `validate` can refuse — same display, same draft, same negative-value
 * refusal — but its `toPatch` additionally folds the freshly-committed value
 * into the record's other components and, when a total can be derived, sends
 * the recomputed `currentValue` **in the same patch**. One `itemInfo` PUT,
 * two keys — the backend's own object-merge write contract (this file's
 * header, trap 1) already makes that safe, so no second commit is needed.
 *
 * **Never used for the total itself.** Editing the total deliberately does
 * not re-derive — a figure taken off the institution's own statement must not
 * be silently overwritten by a derivation from components nobody filled in.
 * The total is always a plain {@link moneyRow}.
 *
 * The derivation arrives as `totalPatch` rather than being hard-coded, and today
 * `nursing-home-bond`'s Refund due ({@link nursingHomeBondTotalPatch}) is its only
 * caller — bound to its own `compute*` helper, so it never restates what a total is.
 *
 * **`bank-account` used this and no longer does** (VALE-967). Its two components are
 * now fields inside the value row's own popover rather than rows beside it, which is
 * what let the total stop being independently typeable: the parts and the figure they
 * add up to are one gesture, so there is no second place for a reader to disagree with
 * themselves. A type wanting that arrangement reaches for {@link valueRow}'s
 * `components`, not for this.
 */
function derivingMoneyRow(
  label: string,
  key: string,
  item: InventoryItem,
  totalPatch: (
    item: InventoryItem,
    key: string,
    parsed: number | null
  ) => Record<string, unknown>,
  { signed = false }: { signed?: boolean } = {}
): AttributeSpec<InventoryPatch> {
  const base = moneyRow(label, key, item, { signed });
  return {
    ...base,
    toPatch: (draft) => {
      const parsed = parseMoneyDraft(draft);
      if (parsed === undefined) return null;

      return {
        itemInfo: {
          [key]: parsed ?? '',
          ...totalPatch(item, key, parsed)
        }
      };
    }
  };
}

/**
 * **A provenance tooltip that cannot leave the drawer** (VALE-355).
 *
 * Radix does collision detection against the **viewport**, so a tooltip anchored
 * near the drawer's left edge happily extends past it and floats over the dimmed
 * list behind — which reads as a stray label belonging to nothing. `collisionBoundary`
 * is the fix, and the boundary is resolved from the trigger's own
 * `closest('[role="dialog"]')` rather than passed in: the row specs are built by
 * plain functions with no access to the host's refs, and the panel is whatever
 * dialog the row happens to be rendered in — the drawer today, the add sheet
 * tomorrow.
 *
 * Read off the ref at render time with no state: `TooltipContent` is only rendered
 * once the tooltip opens, which is necessarily after the trigger has mounted, so the
 * ref is always populated by the time this value is needed.
 *
 * `TooltipPortal` is separately required — the read-out truncates the green line, so
 * the trigger sits inside an `overflow-hidden` ancestor, and unportalled content is
 * painted at full size somewhere the reader is not looking (`ui/tooltip.tsx`
 * documents that trap).
 */
function ProvenanceTooltip({
  tip,
  children
}: {
  tip: string;
  /**
   * Rendered as the sole child of an `asChild` `TooltipTrigger` (→ Slot), which
   * requires a single valid element — `ReactNode` would let a caller pass text
   * and throw at render, since @radix-ui/react-tooltip 1.2.16 already resolves
   * @radix-ui/react-slot 1.3.3 (VALE-1288).
   */
  children: React.ReactElement;
}) {
  const triggerRef = useRef<HTMLElement | null>(null);
  /*
    Resolved when the tooltip opens — an event handler, not render and not an effect.
    Reading a ref during render is a correctness trap (its `current` is not a render
    input), and deriving it in an effect is a cascading `setState`; the open event is
    the moment the boundary is actually needed, and the trigger is mounted by then.
  */
  const [boundary, setBoundary] = useState<Element | null>(null);

  return (
    <Tooltip
      onOpenChange={(open) => {
        if (open) {
          setBoundary(triggerRef.current?.closest('[role="dialog"]') ?? null);
        }
      }}
    >
      {/*
        `asChild`, so the rendered node is the caller's own element rather than the
        trigger's default `<button>` — which is why the ref is typed `HTMLElement`
        rather than the `HTMLButtonElement` the primitive's own signature assumes.
      */}
      <TooltipTrigger
        asChild
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
      >
        {children}
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent collisionBoundary={boundary} collisionPadding={8}>
          {tip}
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

/**
 * **A money figure and its provenance, as ONE row** (VALE-355; `AttributeEditor`'s
 * `valueWithConfirmation` arm).
 *
 * ## Four shapes, and why this is the one
 *
 * 1. **A `money` row plus a `text` row labelled `DOD source`**, whose read-out was
 *    the status sentence `Estimate — not yet confirmed`. Reported broken: *"editing
 *    it doesn't change what the row then shows, and the label gives no indication
 *    of what is being asked for."* Clicking it opened an editor on the note —
 *    empty on an unconfirmed figure — so the row emptied itself on click. The
 *    document half was unreachable and `text` derives no `×`, so the only way to
 *    un-confirm was select-all-delete.
 * 2. **A `money` row plus three rows** (a live `Confirmed` boolean revealing a
 *    picker and a note). Right question, wrong cost: eight rows for one value pair,
 *    and the boolean could not commit alone (the backend refuses a confirmation
 *    with no source), so it needed a `localCommit` plus a per-session reveal flag
 *    to fake one wizard across three independent rows.
 * 3. **A `money` row plus one composite popover row.** Four rows per pair, and the
 *    figure and the source it is read off still lived in two different gestures.
 * 4. **This: one row per figure**, opening a popover that holds the amount, the
 *    `Confirmed` answer, the document and the note. Two rows for the whole VALUE
 *    block.
 *
 * ## Why merging the amount in is a correctness win, not just a compactness one
 *
 * The backend's clear-on-edit invariant nulls a confirmation when the figure it
 * vouched for changes (LF-4356, extended to DOD in LF-5546). Split across two rows
 * that rule is invisible: you edit the balance in one row and a ✓ silently
 * disappears from another. In one popover the two are in front of you together, so
 * a new figure and the statement it came from are stated in the same commit.
 *
 * **What that cost, and how it is paid** (VALE-473). Because the request carries
 * both halves, a figure-only edit still shipped an explicit set-intent — so
 * `reconcileValueConfirmationOnUpdate` re-affirmed the old source against the new
 * number, stamped with the reader's name and today's date, and LF-4356's rule
 * became unreachable from this surface rather than broken. The popover now answers
 * `Confirmed against a source — No` for the reader when the figure moves
 * ({@link AttributeValueConfirmationEditor}), which is the visible form of the same
 * invariant: the intent this row commits is then `null`, and the backend clears.
 *
 * ## At rest it is the figure, then a badge
 *
 * `$450.00 · ✓ Closing statement, p.2` — the amount, then a green tick and whatever
 * vouches for it (the document's name, or the note when there is no document). An
 * unconfirmed figure shows the amount alone; an empty one shows the treatment's own
 * muted `Set <label>…` invitation rather than a status sentence, because a sentence
 * is what read as unclickable the first time round.
 *
 * **`Yes` against nothing is refused here, with a reason** — the backend answers it
 * with a 400, {@link AttributeSpec.validate} answers it in the row and names what is
 * missing. A negative amount is refused the same way unless `signed`.
 */
/**
 * **One of the two estate figures on an item, as a spec** (VALE-1032) — a
 * `valueWithConfirmation` row that additionally says *which* figure it holds.
 *
 * The marker is inventory's own and travels no further than this module's public
 * {@link inventoryValueSpec}: it is an extra property on an ordinary
 * `AttributeSpec`, so `AttributeGrid` neither sees nor needs it, and the shared
 * package stays clear of a domain distinction it has no use for.
 *
 * It exists because the alternatives are both worse. The current-value row's key
 * and label vary by type (`currentValue` · `currentBalance` · `Refund due`), so
 * nothing already on the spec identifies it, and picking it out by position
 * inside the VALUE block would make every future builder's row order a silent
 * contract.
 */
export type InventoryValueSpec = AttributeSpec<InventoryPatch> & {
  valueKind: 'current' | 'dod';
  /**
   * **The `itemInfo` key this figure is stored under** (VALE-1181).
   *
   * Stated rather than derivable, and only because of the composed case: a row
   * with {@link MoneyRowOptions.components} commits its PARTS and lets the
   * backend recompute the total, so its `toPatch` names
   * `statedBalanceAtDateOfDeath` and `accruedInterestAtDateOfDeath` while the
   * figure it draws is `valueAtDateOfDeath`. Anything reading a row's contract
   * keys off its patch therefore misses the very key the row displays.
   *
   * The AI review is what needs it: the diff keys that total
   * `itemInfo.valueAtDateOfDeath`, and without this the change matched no row,
   * fell through to an appended read-out and rendered as a bare `8200` under a
   * truncated label, beside the row that was already showing `$8,200.00`.
   */
  valueInfoKey: string;
  /*
    Narrowed, so a caller holding one of these reads `amountLabel` / `quickFill` /
    `components` straight off it. The union is closed and every row this type
    describes is built by {@link valueRow}, so the narrowing states a fact rather
    than asserting one — and it saves the inventory list a guard per prop against
    a kind that cannot arrive.
  */
  editor: Extract<AttributeEditor, { kind: 'valueWithConfirmation' }>;
};

/**
 * **Whether one of the two estate figures is vouched for**, on either surface
 * that renders these recipes.
 *
 * Confirmed is derived differently on the two. Over a PERSISTED record — the
 * matter's Inventory tab — the record came from the API, so `dodValueConfirmed`
 * is the server's own derivation. Over a BUFFER — the add dialog, Discovery's
 * add card, and both AI review surfaces — the record is a *buffer of request
 * patches*: `toPatch` puts the intent straight into it and there is no server to
 * derive a boolean, so reading `dodValueConfirmed` there reports every buffered
 * source as unconfirmed.
 *
 * That second list gained the review surfaces under VALE-1617, and gaining them
 * is the whole fix: they had been reading the persisted branch, so a figure the
 * reviewer confirmed rendered straight back to `No`. See
 * {@link InventoryBlockContext.bufferedRecord} for why the flag is named for the
 * property rather than for the add dialog.
 *
 * Extracted from {@link valueRow} (VALE-1479) because the share recipes' market
 * figures need the identical answer: their rows are `marketFigure` rather than
 * `valueWithConfirmation`, so they own no confirmation of their own, but the
 * *item* still carries one and the inventory list reads it for exactly these
 * holdings. A second copy of this derivation is how a share row and its own row
 * in the list would start disagreeing about the same figure.
 */
function figureConfirmed(
  context: InventoryBlockContext,
  valueKind: 'current' | 'dod'
): boolean {
  const { item, bufferedRecord = false } = context;
  const confirmation =
    valueKind === 'current'
      ? item.currentValueConfirmation
      : item.dodValueConfirmation;
  return bufferedRecord
    ? confirmation != null
    : (valueKind === 'current'
        ? item.currentValueConfirmed
        : item.dodValueConfirmed) === true;
}

function valueRow(
  label: string,
  key: string,
  valueKind: 'current' | 'dod',
  context: InventoryBlockContext,
  {
    signed = false,
    deriveTotal,
    components,
    deriveAmount,
    derivedAmountCaption,
    amountQualifier
  }: MoneyRowOptions = {}
): InventoryValueSpec {
  const { item, bufferedRecord = false, valueSourceDocuments = [] } = context;
  const amount = infoNumber(item, key);
  const confirmation =
    valueKind === 'current'
      ? item.currentValueConfirmation
      : item.dodValueConfirmation;

  const confirmed = figureConfirmed(context, valueKind);

  const note = confirmation?.source ?? '';
  const sourceFileId =
    confirmation?.sourceFileId != null
      ? String(confirmation.sourceFileId)
      : null;
  const documentName =
    confirmation?.sourceFileName ??
    (sourceFileId
      ? valueSourceDocuments.find((doc) => doc.value === sourceFileId)?.label
      : undefined);

  /*
    `Confirmed against <the source>` reads as a sentence about the figure, which is
    what a green line under it should be.
  */
  const confirmedLine =
    (documentName ?? note.trim())
      ? `Confirmed against ${documentName ?? note.trim()}`
      : 'Confirmed';

  /**
   * **The whole provenance, one string** — the popover's read-only header *and* the
   * green line's hover tooltip (VALE-355). Composed from the same fields, in the
   * same order, as the flag-off form's read caption, so the drawer and the
   * inventory list tell a reader the same story about a figure: what vouches for
   * it, whether AI helped, who said so and when.
   *
   * One value for both surfaces on purpose. The green line is truncated to a single
   * line by design, so the tooltip is the only place the rest is reachable — and a
   * tooltip showing less than the popover would make hovering a worse version of
   * clicking.
   */
  /**
   * **The same sentence, and the same tooltip component, as the inventory list**
   * (VALE-355). {@link confirmationProvenanceTip} is that list's own builder, moved
   * out of `current-view-item-row.tsx` so both surfaces share one definition — a
   * lawyer hovering a ✓ in the list and hovering it in the drawer must not be told
   * two different stories about one figure.
   *
   * It doubles as the popover's read-only header: the green line is one line by
   * design, so a hover showing less than the popover would make hovering a worse
   * version of clicking.
   */
  const provenance = confirmed ? confirmationProvenanceTip(confirmation) : null;

  /**
   * **Whether a model, rather than a person, is what vouches for this figure.**
   *
   * Read once here and used in both places it changes the reading: the green
   * line under the value, and the popover's provenance header. `confirmed` is
   * the gate — an unconfirmed figure has nobody standing behind it, AI or
   * otherwise, so there is nothing for the colour to say.
   */
  const aiConfirmed = confirmed && confirmation?.aiAssisted === true;

  /*
    A negative *liability* balance is a credit — the institution owes the estate
    ([INVDRAW-7] / VALE-78). `ValuePairFields` says so with a caption; here it is
    appended to the figure so a signed negative does not read as an oddity.
  */
  const isCredit = signed && amount != null && amount < 0;

  /**
   * **Nothing vouches for this figure, so it reads as an estimate** (VALE-1479).
   *
   * The inventory list has marked an unconfirmed figure with a leading `~` since
   * LF-4356; this row printed the same number unmarked, so the two surfaces
   * disagreed about how to say *estimate* about one value — and this is the
   * surface the reader is about to act in. {@link isEstimatedFigure} is the
   * shared rule, so a change to what counts as an estimate cannot land on one
   * surface only.
   *
   * **The mark is the whole of it** (VALE-1617): there is no estimate ink any
   * more, so the figure keeps whatever ink its row carries — which is what lets
   * the change ink reach it on the two AI review surfaces.
   *
   * It reads on **both** halves of a value pair, exactly as the list does. The
   * drawer is denser, which was the argument for marking only the date-of-death
   * half — but the mark is one character and the inconsistency it would buy is a
   * reader concluding that a current value with no `~` is vouched for.
   */
  const estimated = isEstimatedFigure(amount, confirmed);

  /**
   * **The date-of-death figure, offered to the current one in a click**
   * (VALE-1032).
   *
   * A current value is very often the date-of-death figure unchanged, so the
   * inventory list's quick-set dialog offered it rather than making the reader
   * read the column beside it and retype. That dialog is gone — this popover
   * answers the same question on both surfaces now — and the offer came with it
   * instead of being dropped, so no reader lost an affordance in the move.
   *
   * **Which key holds that figure is the same derivation the dialog made**: a
   * liability's date-of-death balance is `amountOwed`, every asset's is
   * `valueAtDateOfDeath`. It is a derivation rather than a per-type option
   * because all 22 builders already agree on it — `LIABILITY_TYPE_SLUGS` is the
   * one place that split is decided, so binding to it is what keeps a 23rd type
   * from having to remember.
   *
   * Only ever offered *to* a current row, and never from a row to itself.
   * `null` when there is no recorded figure — but a recorded `$0` is offered
   * like any other, because an account that was nil at death is commonly still
   * nil now (LF-6093, whose whole point was that a nil balance is a fact and not
   * an absence).
   */
  const quickFill = (() => {
    if (valueKind !== 'current') return null;
    const isLiability = LIABILITY_TYPE_SLUGS.has(context.typeSlug);
    const dodKey = isLiability ? 'amountOwed' : 'valueAtDateOfDeath';
    if (dodKey === key) return null;
    const dodAmount = infoNumber(item, dodKey);
    if (dodAmount == null) return null;
    return {
      label: isLiability ? 'date-of-death balance' : 'date-of-death value',
      amount: dodAmount
    };
  })();

  return {
    label,
    /*
      **Which of the two estate figures this row holds** (VALE-1032). Not part of
      the shared `AttributeSpec` — *current* versus *date of death* is an
      inventory fact and the attribute package has no business learning it — but
      the rows a builder returns are otherwise indistinguishable to anything
      outside this module, and {@link inventoryValueSpec} needs to name one to
      hand the inventory list the same row the drawer would open.
    */
    valueKind,
    valueInfoKey: key,
    /*
      **The figure, and the confirmation on its own line underneath** (VALE-355
      round 4). Sharing one line was the first attempt and it read as two competing
      values on a half-drawer cell — the figure is what the row is named for, and a
      long filename beside it pushed the number around and truncated anyway. As a
      quiet green line below, the amount keeps the whole first line and the
      provenance gets the full cell width, so `Confirmed against <a real filename>`
      fits without ellipsising.

      It renders **inside the value's own cell**, so the row is still one hit area
      and one click still opens the popover — the sub-line is part of the read-out,
      not a second row.
    */
    display:
      amount == null ? null : (
        <span className="flex min-w-0 flex-col gap-0.5">
          {/*
            **The estimate treatment is on the amount line, not the whole cell**
            (VALE-1479). The mark belongs in front of the figure, not in front of
            the qualifier or the green confirmation line beneath it.

            It carried a muted ink too, and the argument for scoping it here was
            that muting the whole cell would grey out `Incl. $X accrued
            interest`, a stated fact rather than part of the approximation. The
            ink is gone entirely now (VALE-1617) — it beat the change ink on the
            review surfaces — so only the mark's placement is still at stake.
          */}
          <EstimatedFigure estimated={estimated}>
            {formatCurrency(amount)}
            {isCredit ? ' · recorded as a credit' : ''}
          </EstimatedFigure>
          {amountQualifier ? (
            /*
              **Its own line, not a suffix.** Trailing the figure was the first
              attempt and the value cell is half the drawer: `$88,000.00 (incl.
              $3,000.0…` truncated mid-figure and crowded the number the row is
              named for. As a quiet line underneath it gets the full cell width,
              in the same anatomy the green confirmation line below already uses
              — value first, then what is inside it, then what vouches for it.
            */
            <span
              className={cn(
                ATTRIBUTE_LABEL_TEXT,
                'text-info-subtle-foreground min-w-0 truncate'
              )}
            >
              {amountQualifier}
            </span>
          ) : null}
          {confirmed ? (
            /*
                  **The app's `Tooltip`, not a native `title`** (VALE-355). It is the
                  component the inventory list uses for this exact hover, so the
                  provenance appears with the same delay, styling and placement on
                  both surfaces — a `title` renders as the OS tooltip, which matches
                  nothing else in the product and cannot be styled at all.

                  The line itself stays **one line**: the value cell is half the
                  drawer, a real filename wraps almost always, and two-line rows down
                  the VALUE block cost more than the truncation saves. The hover is
                  what makes the rest reachable.
                */
            <ProvenanceTooltip tip={provenance ?? confirmedLine}>
              <span
                className={cn(
                  ATTRIBUTE_LABEL_TEXT,
                  'flex min-w-0 items-start gap-1',
                  /*
                    **Purple when AI vouched for the figure, green when a person
                    did.** `--ai-subtle-foreground` is the product's "a machine
                    produced this" colour, and this is the line that names who
                    stands behind the number — so it is the line that should
                    answer it, rather than leaving `· AI-assisted` to be found at
                    the end of the sentence. The same swap the inventory list's
                    tick makes, so the two surfaces still tell one story.
                  */
                  aiConfirmed ? 'text-ai-subtle-foreground' : 'text-success'
                )}
              >
                {/*
                        A bare tick, sized in `em` so it tracks the 12px line rather
                        than the theme's 3.4px `--spacing` grid, and nudged onto that
                        line's optical centre. It was a `BadgeCheck` — a badge *with*
                        a tick inside it — which at ~10px reads as a blob;
                        `strokeWidth={3}` keeps a plain `Check` crisp at this size.

                        `mt-[0.25em]`, not `items-center`: centring would align the
                        glyph to the whole block rather than to the first line, which
                        is where the reader's eye is.
                      */}
                <Check
                  aria-hidden
                  strokeWidth={3}
                  className="mt-[0.25em] size-[0.85em] shrink-0"
                />
                <span className="min-w-0 truncate">{confirmedLine}</span>
              </span>
            </ProvenanceTooltip>
          ) : null}
        </span>
      ),
    draft: {
      amount: amount == null ? '' : String(amount),
      confirmed,
      sourceFileId,
      note,
      // The parts open with what the record holds; absent stays an empty string, so
      // a blank part is offered rather than pre-filled with a zero nobody entered.
      ...(components
        ? {
            components: Object.fromEntries(
              components.map(({ key: componentKey }) => {
                const value = infoNumber(item, componentKey);
                return [componentKey, value == null ? '' : String(value)];
              })
            )
          }
        : {})
    },
    editor: {
      kind: 'valueWithConfirmation',
      amountLabel: label,
      signed,
      documents: valueSourceDocuments,
      documentsPlaceholder: context.valueSourceDocumentsPlaceholder,
      provenance,
      provenanceTone: aiConfirmed ? ('ai' as const) : ('muted' as const),
      quickFill,
      components,
      deriveAmount,
      derivedAmountCaption
    },
    copyText: amount == null ? '' : formatCurrency(amount),
    clearTo: {
      amount: '',
      confirmed: false,
      sourceFileId: null,
      note: '',
      // Clearing a composed figure clears what it was composed from — leaving the
      // parts behind would re-derive the value on the next save.
      ...(components
        ? {
            components: Object.fromEntries(
              components.map(({ key: componentKey }) => [componentKey, ''])
            )
          }
        : {})
    },
    validate: (draft) => {
      const next = asValueDraft(draft);
      /*
        **A composed figure is refused part by part** (VALE-967). The total is
        derived, so a complaint about it would point at a field the reader cannot
        edit; naming the part that is actually wrong is what makes the refusal
        actionable — `useAttributeCommit` renders it under the value and holds
        the popover open on the offending input.
      */
      for (const { key: componentKey, label: componentLabel } of components ??
        []) {
        const rawPart = (next.components?.[componentKey] ?? '').trim();
        if (rawPart.length === 0) continue;
        const parsedPart = Number(rawPart);
        if (!Number.isFinite(parsedPart))
          return `${componentLabel} must be an amount.`;
        if (!signed && parsedPart < 0) {
          return `${componentLabel} cannot be negative. Only a liability balance can be a credit.`;
        }
      }

      const raw = next.amount.trim();
      if (raw.length > 0) {
        const parsed = Number(raw);
        if (!Number.isFinite(parsed)) return `${label} must be an amount.`;
        if (!signed && parsed < 0) {
          return `${label} cannot be negative. Only a liability balance can be a credit.`;
        }
      }
      /*
        **A document and a note are both optional** (VALE-355, owner's call). This
        row used to refuse `Confirmed` with neither, mirroring a backend rule that
        did the same; both are gone, so a figure can be marked confirmed on the
        lawyer's own authority and the source recorded when there is one.

        Still refused: confirming a figure that does not exist — there is nothing
        for the ✓ to be about.
      */
      if (!next.confirmed) return null;
      return raw ? null : `Enter ${label.toLowerCase()} before confirming it.`;
    },
    toPatch: (draft) => {
      const next = asValueDraft(draft);
      const parsed = parseMoneyDraft(next.amount);
      if (parsed === undefined) return null;

      const trimmed = next.note.trim();
      const fileId = next.sourceFileId ? Number(next.sourceFileId) : NaN;
      const hasFile = Number.isFinite(fileId);
      /*
        **Not confirmed, or confirmed against nothing, means `null`** — the
        un-confirm intent. Never `{}`: the backend reads the intent as a tri-state
        and would 400 on an empty object where the reader meant "clear".
      */
      const intent = next.confirmed
        ? {
            ...(trimmed ? { source: trimmed } : {}),
            ...(hasFile ? { sourceFileId: fileId } : {})
          }
        : null;

      /*
        One PUT carrying the figure and its provenance. `deriveTotal` is the
        nursing-home-bond case ([INVDRAW-11] / VALE-82's derive-but-don't-clobber
        rule): a component figure additionally folds into the record's recomputed
        total, in the same patch, because the backend merges `itemInfo` objects.
      */
      /*
        **A composed figure commits its parts, not the total** (VALE-967). The
        total is never typed here, and the backend derives it from these very keys
        on every write — sending it back would restate a rule that already has one
        owner. The confirmation intent rides along in the same request, which is
        what lets a new figure and the statement it came from be stated together.
      */
      if (components) {
        const parts: Record<string, unknown> = {};
        for (const { key: componentKey } of components) {
          const raw = next.components?.[componentKey];
          const value = parseMoneyDraft(raw ?? '');
          // A part left mid-edit as an unparseable string refuses the whole commit,
          // exactly as a bad amount does — never a silent partial write.
          if (value === undefined) return null;
          parts[componentKey] = value ?? '';
        }

        return {
          itemInfo: {
            ...parts,
            /*
              **Over a buffer the patch carries the derived total as well**
              (VALE-1349). The paragraph above is the persisted contract and stays
              true there: a PUT names the parts, the backend recomputes the total
              from them, and the refetched record is what the row reads back.

              A buffered record has no server between a commit and the next
              render. It is the buffer `applyPatch` merges into, so a patch naming
              only the parts leaves `valueAtDateOfDeath` absent from it — and the
              row draws itself from exactly that key. The reader's figure was
              accepted and stored, and the row still came back reading
              `Set DOD value…`, which is indistinguishable from a
              refusal. It also emptied the current row's quick-fill offer, which
              reads the same key.

              This reached the AI review surfaces with the rename (VALE-1617):
              a composed money row on an extracted item has no server either, so
              it had the same silent hole.

              So the buffer derives what the backend would have derived, through
              this row's own {@link MoneyRowOptions.deriveAmount} — the derivation
              already declared on the spec, not a second copy of the rule. This is
              not a restatement reaching the API either: `createInventoryItem`
              runs `normaliseBankAccountDodValue` over the merged blob like every
              other write path, so the total it stores is recomputed from these
              parts whatever the request says about it.

              `''` rather than an omission when nothing derives: clearing every
              part must clear the figure they added up to, and an absent key would
              leave the previous total standing in the buffer.
            */
            ...(bufferedRecord && deriveAmount
              ? { [key]: deriveAmount(next.components ?? {}) ?? '' }
              : {})
          },
          ...(valueKind === 'current'
            ? { currentValueConfirmation: intent }
            : { dodValueConfirmation: intent })
        };
      }

      const info: Record<string, unknown> = { [key]: parsed ?? '' };
      if (deriveTotal) Object.assign(info, deriveTotal(parsed));

      return {
        itemInfo: info,
        ...(valueKind === 'current'
          ? { currentValueConfirmation: intent }
          : { dodValueConfirmation: intent })
      };
    }
  };
}

/** Options a merged value row takes beyond its key. */
interface MoneyRowOptions {
  signed?: boolean;
  /**
   * Extra `itemInfo` keys a committed component figure implies — today only
   * `nursing-home-bond`'s recomputed `currentValue` (see {@link valueRow}).
   */
  deriveTotal?: (parsed: number | null) => Record<string, unknown>;
  /**
   * The `itemInfo` keys the figure is composed from, edited inside this row's own
   * popover (VALE-967). The amount becomes their running total, and `toPatch`
   * commits the parts rather than the total — the backend derives it again on
   * write, and that copy is the authoritative one.
   */
  components?: Array<{ key: string; label: string }>;
  /** The running total, given the raw component drafts. Bound to a shared helper. */
  deriveAmount?: (values: Record<string, string>) => number | undefined;
  /** One line under the composed figure naming what it is made of. */
  derivedAmountCaption?: string;
  /**
   * A quiet line under the figure in the **read-out**, naming the part of a composed
   * value worth seeing without opening the popover (VALE-967).
   *
   * Folding the parts into the popover put the whole date-of-death fact in one
   * place, but it also put the breakdown one click away, and *"is the interest in
   * this number?"* is a question a lawyer asks while scanning rather than while
   * editing. It renders inside the value's own cell, so the row is still one hit
   * area and one click still opens the popover.
   *
   * Say `null` when there is nothing worth qualifying: a nil interest is a recorded
   * fact but *"Incl. $0.00"* is noise on every account that earned none.
   */
  amountQualifier?: string | null;
}

/**
 * **The extra `itemInfo` a nursing-home-bond component figure implies** — the
 * recomputed `currentValue` total ([INVDRAW-11] / VALE-82's derive-but-don't-clobber
 * rule), folded into the same patch because the backend merges `itemInfo` objects.
 *
 * Bound to {@link computeNursingHomeBondTotal} rather than re-deriving the sum, and
 * shared with {@link derivingMoneyRow} — the four component rows that carry no
 * confirmation still go through that helper, and the two must not drift on what a
 * total is.
 */
function nursingHomeBondTotalPatch(
  item: InventoryItem,
  key: string,
  parsed: number | null
): Record<string, unknown> {
  const info = item.itemInfo as NursingHomeBondComponents | null;
  const total = computeNursingHomeBondTotal({
    ...(info ?? {}),
    [key]: parsed
  } as NursingHomeBondComponents);
  return total !== undefined ? { currentValue: total } : {};
}

/*
  `bankAccountDodValuePatch` lived here until VALE-967 — it folded a recomputed
  `valueAtDateOfDeath` into a component row's patch, back when the two components
  were rows of their own. They are now fields inside the value row's popover, which
  commits them directly and lets the backend derive the total, so nothing writes that
  key from the client any more. The bond keeps its equivalent: on a bond the total is
  `currentValue` and `valueAtDateOfDeath` is the RAD principal, a component — the
  mirror image, and still a row-per-component surface.
*/

/** Narrow the grid's draft union to this arm's own shape. */
function asValueDraft(draft: AttributeDraft): AttributeValueConfirmation {
  return draft && typeof draft === 'object' && 'confirmed' in draft
    ? (draft as AttributeValueConfirmation)
    : { amount: '', confirmed: false, sourceFileId: null, note: '' };
}

/**
 * **`bank-account` — the pilot** (`[INVDRAW-5]` / VALE-76). `bank-account` is
 * Tier 1, the shape 14 of the 22 types share, so this recipe is the one
 * `[INVDRAW-6…13]` follow — see this ticket's `plan.md` `### Actuals` for the
 * full write-up.
 *
 * Ordered as the flag-off form reads: institution, account identifiers, the
 * value pair with its per-value provenance, then Notes / Non-estate /
 * Estate reference.
 */
function bankAccountBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');
  const accruedInterest = infoNumber(item, 'accruedInterestAtDateOfDeath');

  const blocks: (AttributeBlock<InventoryPatch> | null)[] = [
    // The eyebrow names the field; the kept component suppresses its own
    // label (`hideLabel`) so the fact isn't stated twice — three arrangements
    // were tried on the prototype before this one, see its header note.
    { kind: 'heading', title: 'DETAILS' },
    {
      /*
        Two columns, inherited. This was `columns: 1` because "Account number"
        truncated against a label column that was only 84px — a width the 640px
        drawer forced. VALE-294 widened the surface to 57%, the column the
        container query yields is ~104px, and `bank-account` measures with **no
        clipped label at all**. Re-measured, not assumed.
      */
      kind: 'rows',
      title: null,
      fields: [
        /*
          **The account holder, as the bank has it, first** (VALE-1940; the
          owner's call at the VALE-1936 sign-off). It is what a statement or a
          letter from the bank names, so it heads the record above the numbers.
        */
        infoTextRow(item, 'Account name', 'accountName', { wraps: true }),
        pickerRow('Bank', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Bank"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        /*
          **The account as one row** (VALE-1211). It was `BSB` and
          `Account number` as two `text` rows, each with its own commit — and on
          this path every commit is its own `PUT`, which is the same hazard
          `shareFieldsPatch` above exists for: two writes in the same tick raced
          in the backend's `itemInfo` merge and whichever landed last erased the
          other's key. One popover commits both keys in one patch, so that race
          is unreachable here rather than defended against.

          No account name and no verification here: the name is its own row
          above (VALE-1940) and this item type stores no verification.
        */
        {
          label: 'Bank account',
          // Owns its line — see `contact-attribute-specs.ts`'s row. The 640px
          // drawer's half-column is the narrowest of the four surfaces.
          fullWidth: true,
          display: formatBankAccountSummary(bankAccountDraft(item)) || null,
          draft: bankAccountDraft(item),
          // The two numbers, without the read-out's `·`.
          copyText: bankAccountCopyText(bankAccountDraft(item)),
          editor: { kind: 'bankAccount' },
          /*
            Still bound to `bsbSchema` / `accountNumberSchema` rather than
            restating their rules, exactly as the two rows this replaces were
            (LF-6201, VALE-111): they preprocess with `stripFormattingSeparators`,
            so the separator `AttributeBankAccountEditor` inserts is stripped by
            the schema that already knew how to — nothing here had to learn about
            the new formatting.
          */
          validate: (draft) => {
            if (draft === null || typeof draft !== 'object') return null;
            const account = draft as AttributeBankAccount;

            const bsb = (account.bsb ?? '').trim();
            if (bsb) {
              const parsed = bsbSchema.safeParse(bsb);
              if (!parsed.success) {
                return parsed.error.issues[0]?.message ?? 'Invalid BSB';
              }
            }

            const number = (account.accountNumber ?? '').trim();
            if (number) {
              const parsed = accountNumberSchema.safeParse(number);
              if (!parsed.success) {
                return (
                  parsed.error.issues[0]?.message ?? 'Invalid account number'
                );
              }
            }

            return null;
          },
          /*
            Commit each schema's own normalised output, not the raw draft, so a
            separator-formatted value is stored digits-only exactly as the
            flag-off form stores it (LF-6201, VALE-111) — unchanged from the two
            rows this replaces, only now in one patch.

            Built on `infoPatch` rather than restating its empty-string contract,
            the same way `shareFieldsPatch` is: a cleared `info` field must land
            as `''`.
          */
          toPatch: (draft) => {
            const account =
              draft && typeof draft === 'object'
                ? (draft as AttributeBankAccount)
                : null;
            const normalised = (
              schema: typeof bsbSchema,
              value: string | null | undefined
            ) => {
              const raw = (value ?? '').trim();
              if (!raw) return '';
              const parsed = schema.safeParse(raw);
              return parsed.success && typeof parsed.data === 'string'
                ? parsed.data
                : raw;
            };
            return {
              itemInfo: {
                ...infoPatch('bsb', normalised(bsbSchema, account?.bsb))
                  .itemInfo,
                ...infoPatch(
                  'accountNumber',
                  normalised(accountNumberSchema, account?.accountNumber)
                ).itemInfo
              }
            };
          }
        }
      ]
    },
    {
      /*
        Two columns, inherited — "Current balance" / "Current source" clipped
        at the old 84px column and fit the widened ~104px one (measured: no
        clipped label on `bank-account`). The rows stay **interleaved** (money,
        its confirmation, money, its confirmation) rather than grouped, which
        was always the separate half of this comment's reasoning: at two
        columns it puts each figure directly beside its own provenance rather
        than making two unrelated lists.
      */
      kind: 'rows',
      title: null,
      fields: [
        /*
          **Shown, never typed** (VALE-967). This figure is the sum of the two
          components below, so `derivedAmount` drops the popover's amount input
          and leaves its confirmation half intact — the ✓ still vouches for the
          date-of-death value, which is what `deriveConfirmableDodValue` computes
          it against on the backend, and editing either component moves the total
          and so clears a stale one.

          It was an editable `valueRow` until VALE-967. That let a reader type a
          total over components that said something else and then confirm it: the
          reported state was $42,000.00 marked Confirmed above figures adding to
          $41,000.00, with only a passive grey line noting the difference.

          **`DOD value`, not `Date of death value`** (VALE-1479). VALE-967 spelled
          it in full because `DOD value` then sat directly above a section headed
          `DATE OF DEATH VALUE` — the same quantity named twice, which is what made
          the pair read as two rival figures. That heading no longer exists:
          VALE-863 collapsed every recipe to one `DETAILS` eyebrow, so the reason
          stopped describing the surface and the exception goes with it. What is
          left is the argument VALE-1008 made for the share rows, which applies
          here identically — the shared label column clips at roughly 104px, and
          the long form reads back as `Date of death v…` directly beside a money
          figure.
        */
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context, {
          /*
            The two parts live in this row's own popover, under the total they
            add up to (VALE-967) — not in a section of their own, and not as a
            typeable total. A bank reports the balance and the credit interest
            earned but unpaid; the asset's value on that date is their sum.
            Exactly two, deliberately not the bond's five: held credit is an
            aged-care concept and fees and retention are deductions from a
            refund.

            Both labels name what the bank actually said, and the second says
            which side of the death it accrued on. `Interest accrued` alone
            omitted the half that decides the answer — interest accrued *since*
            death is the opposite quantity, is the estate's own income, and six
            production rows still carry it under the retired `accruedInterest`
            key.
          */
          components: [
            {
              key: 'statedBalanceAtDateOfDeath',
              label: 'Balance stated by bank'
            },
            {
              key: 'accruedInterestAtDateOfDeath',
              label: 'Interest accrued to death'
            }
          ],
          deriveAmount: (values) =>
            computeBankAccountDodValue({
              statedBalanceAtDateOfDeath: toNumberOrUndefined(
                values.statedBalanceAtDateOfDeath
              ),
              accruedInterestAtDateOfDeath: toNumberOrUndefined(
                values.accruedInterestAtDateOfDeath
              )
            }),
          derivedAmountCaption:
            'Balance stated by the bank plus interest accrued to death.',
          /*
            The interest, surfaced on the row so the breakdown does not cost a
            click. It is the half a reader is actually checking for — the bare
            balance is what the figure looks like anyway, and whether the interest
            is inside it is the question VALE-803 found 17 accounts answering
            wrongly. Only when there is some: a bank that reported no accrued
            interest is a recorded fact, but `incl. $0.00` on every such account
            is noise.
          */
          amountQualifier: accruedInterest
            ? `Incl. ${formatCurrency(accruedInterest)} accrued interest`
            : null
        }),
        valueRow('Current balance', 'currentBalance', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          /*
            Only matters once the facility is locked in — the flag-off form
            shows it under the identical condition
            (`!isInstitutionEditable && !hideEstateReferenceField`).
            Declared, never omitted, so it animates in the moment the facility
            locks (components/attributes/CLAUDE.md §5).
          */
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];

  return blocks.filter(
    (block): block is AttributeBlock<InventoryPatch> => block !== null
  );
}

/**
 * **The five Tier-1 assets that need only what the pilot established**
 * (`[INVDRAW-6]` / VALE-77): `superannuation`, `life-insurance`,
 * `managed-funds`, `private-company`, `other`. Each follows
 * `bankAccountBlocks`' recipe verbatim — the value pair as `moneyRow` +
 * `confirmationRow` pairs, `infoPatch` for every `itemInfo` write. (This
 * recipe was `columns: 1` throughout until VALE-294, as the escape from a
 * two-column label column that was only 84px; widening the surface removed
 * the need, so every block now inherits the shared two columns.) See this ticket's `plan.md`
 * `### Actuals` for the full write-up, including two acceptance-brief
 * corrections made before implementing (a stale cross-reference to
 * `real-estate`'s fields, and an `other` gating behaviour that matches
 * neither the shipped form nor the prototype).
 */

/**
 * **`superannuation`.** Institution kept under a `FUND` eyebrow — the
 * flag-off form's own `institutionTypeName="Fund"`. `itemInfo.superannuation`
 * (a legacy pre-institution-object field) is not rendered by
 * `SuperannuationForm.tsx` in either mode, so it gets no row — the same
 * "schema holds a field the form never surfaces" case `[INVDRAW-6]`'s
 * `### Actuals` names.
 */
function superannuationBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Fund', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Fund"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Member number',
          display: infoString(item, 'memberNumber') || null,
          draft: infoString(item, 'memberNumber'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('memberNumber', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          // Matches bank-account's identical gate — only matters once the
          // facility is locked in.
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **`life-insurance`.** Institution kept under a `POLICY` eyebrow — the
 * record itself is the policy, so it names the section rather than the
 * institution's role (`institutionTypeName="Insurance Company"` still names
 * the field itself, matching `LifeInsuranceForm.tsx`). `itemInfo.insuranceCompany`
 * (the legacy pre-institution-object field) is not rendered by the flag-off
 * form and gets no row, same as superannuation's `itemInfo.superannuation`.
 */
function lifeInsuranceBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Insurance Company', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Insurance Company"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Policy name',
          display: infoString(item, 'nameOfPolicy') || null,
          draft: infoString(item, 'nameOfPolicy'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('nameOfPolicy', draft) : null
        },
        {
          label: 'Policy number',
          display: infoString(item, 'policyNumber') || null,
          draft: infoString(item, 'policyNumber'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('policyNumber', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **`managed-funds`.** Identical shape to `superannuation` — a Member number
 * beside the kept fund search — so the two share a recipe rather than a
 * function; `ManagedFundsForm.tsx` and `SuperannuationForm.tsx` are
 * themselves near-identical for the same reason. No legacy unrendered field
 * here (unlike superannuation/life-insurance): `managedFundsSchema` carries
 * none.
 */
function managedFundsBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Managed Fund', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Managed Fund"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Member number',
          display: infoString(item, 'memberNumber') || null,
          draft: infoString(item, 'memberNumber'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('memberNumber', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **`private-company`.** The one Tier-1 asset with **no institution field** —
 * `privateCompanyForm.tsx`'s own header: "not notifiable: no institution to
 * write to, lawyer enters details and valuation directly" — so this recipe
 * has no heading + kept-component pair at all, the same shape `vehicleBlocks`
 * takes for the same reason. `Number of Shares Held` is a plain `number` row,
 * not `money` — rendering a share count as `$1,200.00` would be wrong (this
 * ticket's own acceptance bar) — and is shortened to `Shares held` for the
 * 101px/153px label column, the same "real design work per type" `[INVDRAW-5]`
 * established.
 */
function privateCompanyBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item } = context;
  const notes = infoString(item, 'notes');
  const numberOfShares = infoNumber(item, 'numberOfShares');

  return [
    {
      kind: 'rows',
      title: 'DETAILS',
      fields: [
        {
          label: 'Company name',
          display: infoString(item, 'companyName') || null,
          draft: infoString(item, 'companyName'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('companyName', draft) : null
        },
        {
          label: 'ACN',
          display: infoString(item, 'acn') || null,
          draft: infoString(item, 'acn'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('acn', draft) : null
        },
        {
          label: 'ABN',
          display: infoString(item, 'abn') || null,
          draft: infoString(item, 'abn'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('abn', draft) : null
        },
        {
          label: 'Shares held',
          display: numberOfShares == null ? null : String(numberOfShares),
          draft: numberOfShares == null ? '' : String(numberOfShares),
          editor: { kind: 'number' },
          toPatch: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return infoPatch('numberOfShares', null);
            const parsed = Number(raw);
            return Number.isFinite(parsed)
              ? infoPatch('numberOfShares', parsed)
              : null;
          },
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = Number(raw);
            return Number.isFinite(parsed) && parsed >= 0
              ? null
              : 'Shares held must be 0 or more.';
          }
        },
        {
          label: 'Held',
          display:
            formatSelectLabel(
              infoString(item, 'ownershipType') || undefined,
              PRIVATE_COMPANY_OWNERSHIP_OPTIONS
            ) || null,
          draft: infoString(item, 'ownershipType'),
          editor: {
            kind: 'select',
            options: PRIVATE_COMPANY_OWNERSHIP_OPTIONS.map(
              ({ value, label }) => ({ value, label })
            )
          },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('ownershipType', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        }
        // No Estate reference row — `PrivateCompanyForm.tsx` never accepts
        // `isInstitutionEditable`/`hideEstateReferenceField` at all, since
        // there is no institution/facility-lock workflow to gate it on.
      ]
    }
  ];
}

/**
 * **`other`.** The catch-all asset. Institution kept under an `ORGANISATION`
 * eyebrow, **unconditionally** — corrected on this ticket (see the header
 * note above): the shipped `OtherForm.tsx` renders its institution search
 * with no boolean toggle anywhere in the component, so this recipe puts it
 * first under a heading exactly like superannuation / life-insurance /
 * managed-funds, for the same reason those three do (and for visual
 * consistency across this ticket's five types) rather than reproducing the
 * flag-off form's exact tab order (Description before Organisation). The
 * value-pair key is `currentBalance`, not `currentValue` — `otherSchema`'s own
 * naming, unlike every other type in this ticket. `hasOrganisation` /
 * `institutionName` / `emailAddress` (all superseded by the top-level
 * `institution` object; `emailAddress` is rendered by no mode of
 * `OtherForm.tsx`) get no rows, the same "schema holds a field the form never
 * surfaces" case this ticket's `plan.md` correction names.
 */
function otherBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');
  // The same string-or-object union `InstitutionSearchField` accepts as `value`,
  // reduced to a display name for the hint below. Read off `item` rather than
  // held in state so it follows the drawer's own re-render when the row is set.
  const institutionDisplayName =
    typeof item.institution === 'object' &&
    item.institution !== null &&
    'name' in item.institution
      ? (item.institution as { name?: string }).name || null
      : typeof item.institution === 'string'
        ? item.institution || null
        : null;

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      /*
        **This row decides whether Vale writes to anyone about the asset, so it
        says so** (VALE-783).

        The organisation search below reads as an optional contact detail and is
        nothing of the sort: naming an organisation is what gets a notification of
        death and a collection request drafted to them, and what puts the asset on
        Discovery and Redemption. Leaving it blank makes the asset the executor's
        to collect, with no correspondence at all. A solicitor recording a term
        deposit at a finance company met the second outcome expecting the first,
        and nothing on the surface accounted for the difference — the only
        evidence was correspondence that never appeared.

        A `component` block because this is a caption belonging to the section
        rather than a value of its own — the treatment has no label/value pair to
        express here, which is exactly the case §7b keeps `component` for.
      */
      kind: 'component',
      title: null,
      /*
        **The missing-organisation case is a caution, and reads as one**
        (VALE-1940). As a muted caption it looked like help text, and a reader
        did not take it as Vale telling them nothing will be sent. The standard
        `Banner` says so; the named case stays a quiet caption, because a
        banner on every properly recorded asset would be noise.
      */
      component: institutionDisplayName ? (
        <p className="text-muted-foreground px-1.5 text-xs">
          {`Vale will write to ${institutionDisplayName} about this asset.`}
        </p>
      ) : (
        <Banner
          tone="caution"
          title="Vale will not write to anyone about this asset"
          line="No organisation is named. Name the one holding it if it needs to be notified."
        />
      )
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow(
          /*
            It has to exist at all: collapsing the `ORGANISATION` eyebrow into
            `DETAILS` dropped the search with the heading, leaving the VALE-783
            caption directly below telling a reader to "name the organisation
            holding it" with no control that could. Naming it is what drafts the
            notification of death and the collection request, so the loss was a
            correspondence one, not a cosmetic one.
          */
          'Organisation',
          () => (
            <InstitutionSearchField
              value={item.institution}
              onChange={context.onInstitutionChange ?? (() => {})}
              onInstitutionSearch={context.onInstitutionSearch}
              institutions={context.institutions ?? []}
              loadingInstitutions={context.loadingInstitutions ?? false}
              mode="edit"
              institutionTypeName="Organisation"
              disabled={!isInstitutionEditable}
              hideLabel
              variant="compact"
              /*
                The one picker that offers a type on its create step
                (VALE-1577). Every other row here names a type already — a
                Bank Account's organisation can only be a bank — so this is
                the only place the reader knows something the surface does
                not. Left off, an organisation minted here is filed as
                `other` whatever it actually is. `"asset"` because `otherBlocks`
                only ever renders for the asset `other` type — a liability-only
                institution type must not reach this picker (VALE-1595).
              */
              allowTypeSelection="asset"
            />
          )
        ),
        descriptionRow(item),
        {
          label: 'Phone number',
          display: infoString(item, 'phoneNumber') || null,
          draft: infoString(item, 'phoneNumber'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('phoneNumber', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentBalance', 'current', context)
      ]
    },
    { kind: 'heading', title: 'INSTRUCTIONS' },
    otherInstructionBlock(context),
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **The "other" asset's instruction, as a kept block** (VALE-1940).
 *
 * Declares `itemInfo.instruction` so the AI review marks it and a register-only
 * matter drops it with the other instruction blocks ({@link withoutInstructionBlocks}).
 * A saved record answers through the instruction endpoint; a buffered one (the
 * add dialog, the AI review) writes the answer into its own patch, with no
 * recipients, since those are not saved from an AI apply.
 */
function otherInstructionBlock(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch> {
  const { item, contacts = [], entitlementBeneficiaryShares } = context;
  const info = (item.itemInfo ?? {}) as Record<string, unknown>;
  const instruction =
    typeof info.instruction === 'string' && info.instruction
      ? (info.instruction as OtherInstruction)
      : null;
  const beneficiaryIds = Array.isArray(info.transferBeneficiaries)
    ? (info.transferBeneficiaries as Array<{ contactId?: number }>).flatMap(
        (b) => (typeof b?.contactId === 'number' ? [b.contactId] : [])
      )
    : [];
  const shared = {
    instruction,
    beneficiaryIds,
    contacts,
    entitlementBeneficiaryShares
  };

  return {
    kind: 'component',
    title: null,
    diffKeys: ['itemInfo.instruction'],
    component:
      !context.bufferedRecord && context.matterId ? (
        <SavedOtherInstructionKept
          {...shared}
          matterId={context.matterId}
          itemId={item.id}
        />
      ) : (
        <OtherInstructionKept
          {...shared}
          allowBeneficiaries={false}
          onChange={(request) =>
            context.onCommit?.({
              itemInfo: { instruction: request.instruction ?? '' }
            })
          }
        />
      )
  };
}

/**
 * **`employment-entitlements`** (VALE-1940): unpaid wages, leave and final pay
 * owed by the deceased's employer. It rendered {@link baselineBlocks} until
 * then, so the description and both figures the AI proposes had no row at all.
 *
 * The `other` asset's shape without its instruction and phone number, which
 * this type does not carry: the employer is the organisation Vale writes to
 * (the `employment-entitlements` letter), then what is owed and when.
 */
function employmentEntitlementsBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Employer', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Employer"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        descriptionRow(item)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentBalance', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **`nursing-home-bond`** (`[INVDRAW-11]` / VALE-82) — the first derived value
 * on this surface. `computeNursingHomeBondTotal` (`lib/utils/nursing-home-bond.ts`,
 * shipped and untouched by this ticket) derives Refund Due from the RAD
 * principal plus credit and interest, less fees and retention.
 *
 * Same shape as `bank-account`'s value pair (a money row + its confirmation,
 * per figure), with the RAD/Refund pair's *own* wording rather than the
 * generic DOD/Current one — production already named these domain-specifically
 * (`AgedCareForm.tsx`'s `RAD Amount` / `Refund Due`), and `[INVDRAW-5]`'s
 * "real design work per type" applies here too.
 *
 * The four refund adjustments — like the RAD principal above them — are each
 * a {@link derivingMoneyRow}: committing any of the five re-derives Refund
 * Due in the same patch. Only `currentValue` (Refund Due itself) is a plain
 * `moneyRow` — the derive-but-don't-clobber asymmetry the ticket is named for.
 */
function nursingHomeBondBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');

  const derived = computeNursingHomeBondTotal(
    (item.itemInfo ?? {}) as NursingHomeBondComponents
  );
  const reconciled = isNursingHomeBondTotalReconciled(
    (item.itemInfo ?? {}) as NursingHomeBondComponents & {
      currentValue?: unknown;
    }
  );
  // Never on partial data: `isNursingHomeBondTotalReconciled` already returns
  // `undefined` (not `false`) when there is nothing yet to compare.
  const disagrees = reconciled === false && derived !== undefined;

  const blocks: (AttributeBlock<InventoryPatch> | null)[] = [
    { kind: 'heading', title: 'DETAILS' },
    {
      // Two columns, inherited. "Refund source" clipped at the old 84px label
      // column; at the widened ~104px one it fits — this block measures clean.
      kind: 'rows',
      title: null,
      fields: [
        // Joins the field flow with the other 17 (VALE-997) — it was one of the
        // two left as a `component` block, which is what made the same control
        // read differently here than on every other drawer.
        pickerRow('Facility', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Facility"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        /*
          **The one date-of-death row that keeps a name of its own** (VALE-1479,
          which put every other recipe on the short `DOD value` / `DOD balance`
          pair). It is not an exception to that vocabulary so much as outside it:
          this row does not name a *date*, it names the instrument. A bond's
          date-of-death figure IS the Refundable Accommodation Deposit, the pair
          reads `RAD amount` / `Refund due` rather than date-of-death / current,
          and a facility's own paperwork calls it the RAD. Renaming it `DOD value`
          would trade a term the reader is holding a statement about for one that
          says only when it was measured.
        */
        valueRow('RAD amount', 'valueAtDateOfDeath', 'dod', context, {
          deriveTotal: (parsed) =>
            nursingHomeBondTotalPatch(item, 'valueAtDateOfDeath', parsed)
        }),
        valueRow('Refund due', 'currentValue', 'current', context)
      ]
    },
    {
      /*
        Two columns, inherited — with one **known, accepted** clip:
        "Retention deducted" measures 118px against the widened ~104px column.
        It was `columns: 1` for exactly this reason when the column was 84px;
        widening the surface narrowed the gap but did not close it. Carried
        rather than fixed, because restoring the override would cost this block
        the two-column layout — one of the 18 residual clips VALE-294's
        `plan.md` records against a one-column baseline of 3.
      */
      kind: 'rows',
      title: 'REFUND ADJUSTMENTS',
      fields: [
        derivingMoneyRow(
          'Credit held',
          'heldCredit',
          item,
          nursingHomeBondTotalPatch
        ),
        derivingMoneyRow(
          'Interest accrued',
          'accruedInterest',
          item,
          nursingHomeBondTotalPatch
        ),
        derivingMoneyRow(
          'Fees deducted',
          'feesDeducted',
          item,
          nursingHomeBondTotalPatch
        ),
        derivingMoneyRow(
          'Retention deducted',
          'retentionDeducted',
          item,
          nursingHomeBondTotalPatch
        )
      ]
    },
    // The reconciliation hint — never two figures that cannot both be right.
    // `title: null` continues the REFUND ADJUSTMENTS section rather than
    // inventing a second heading for two sentences of prose. Omitted from the
    // array entirely (not merely hidden) when there is nothing to reconcile,
    // matching the empty-section rule (components/attributes/CLAUDE.md §7b) —
    // a `component` block always counts, so conditioning it here is the only
    // way to keep an agreeing bond from showing an empty section.
    disagrees
      ? {
          kind: 'component' as const,
          title: null,
          component: (
            <p className="text-muted-foreground text-xs">
              Components add up to {formatCurrency(derived)}, which differs from
              the recorded Refund due.
            </p>
          )
        }
      : null,
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          /*
            Only matters once the facility is locked in — matches bank-account's
            identical gate (components/attributes/CLAUDE.md, [INVDRAW-5]).
          */
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];

  return blocks.filter(
    (block): block is AttributeBlock<InventoryPatch> => block !== null
  );
}

/**
 * **The property address, collapsed to one row** (`[INVDRAW-9]` / VALE-80 —
 * the treatment's clearest win on inventory). `RealEstateForm` and
 * `MortgageFormEdit` each render six controls (`AddressSearch` plus Street
 * Address / Suburb / State / Postcode / Country) for the same five
 * flat `itemInfo` keys `address`'s draft already carries.
 *
 * **The AU-state-enum check lives here, self-contained** — `refineAddressState`
 * validates `state` against the *same row's own* `country`, so unlike the
 * title reference below it needs no sibling read at all. Reuses the schema's
 * own `AU_STATE_CODES` / `isAustralianAddress` rather than re-deriving the
 * alias set (root `CLAUDE.md` § "Bind to the source of truth").
 */
function addressRow(
  item: InventoryItem,
  /**
   * A pre-structured single-string address key to fall back to (VALE-302).
   *
   * `utility-bill` recorded its service address as one free-text
   * `propertyAddress` string long before this row existed. Pointing the
   * structured editor at that type without this would render every existing
   * bill's address **blank**, with the old value stranded in the blob — which
   * from the reader's side is indistinguishable from having lost it. So the
   * legacy string seeds `streetAddress` when no structured part is present,
   * and {@link addressRow}'s own `toPatch` retires the key on the first
   * structured write. No migration needed, and a `real-estate` caller that
   * passes nothing behaves exactly as before.
   */
  legacyKey?: string
): AttributeSpec<InventoryPatch> {
  const info = item.itemInfo as Record<string, unknown> | null;
  const part = (key: string): string | undefined => {
    const value = info?.[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  };
  const legacy = legacyKey ? part(legacyKey) : undefined;
  const structured: PostalAddress = {
    streetAddress: part('streetAddress'),
    suburb: part('suburb'),
    state: part('state'),
    postcode: part('postcode'),
    country: part('country')
  };
  const hasStructured = Object.values(structured).some(Boolean);
  const draft: PostalAddress =
    !hasStructured && legacy
      ? { ...structured, streetAddress: legacy }
      : structured;
  const formatted = [
    draft.streetAddress,
    draft.suburb,
    draft.state,
    draft.postcode,
    draft.country
  ]
    .filter(Boolean)
    .join(', ');

  return {
    label: 'Property address',
    display: formatted || null,
    draft,
    /*
      The state is picked, not typed, for an Australian address (VALE-1940):
      the AI field registry allows exactly the state codes, so a free-text
      "Victoria" here would be a value the AI could never propose.
    */
    editor: {
      kind: 'address',
      stateOptions: AUSTRALIAN_STATES.map(({ value, label }) => ({
        value,
        label
      }))
    },
    copyable: true,
    copyText: formatted,
    fullWidth: true,
    clearTo: null,
    validate: (nextDraft) => {
      if (typeof nextDraft === 'string' || nextDraft == null) return null;
      // `AttributeDraft` widened past `string | PostalAddress | null` for the
      // `name` kind's own draft (VALE-129) — this row's `editor.kind` is
      // always `address`, so it only ever holds a `PostalAddress`.
      const address = nextDraft as PostalAddress;
      const state = address.state?.trim();
      if (!state) return null;
      if (
        isAustralianAddress(address.country) &&
        !AU_STATE_CODES.includes(state as (typeof AU_STATE_CODES)[number])
      ) {
        return `State must be one of ${AU_STATE_CODES.join(', ')} for an Australian address`;
      }
      return null;
    },
    toPatch: (nextDraft) => {
      if (typeof nextDraft === 'string') return null;
      const address = nextDraft as PostalAddress | null;
      return {
        itemInfo: {
          streetAddress: address?.streetAddress ?? '',
          suburb: address?.suburb ?? '',
          state: address?.state ?? '',
          postcode: address?.postcode ?? '',
          country: address?.country ?? '',
          // Retire the legacy single-string key the moment structured parts
          // exist, so the two can never disagree about one address. `''` is
          // how `infoPatch` clears a value — the backend's own
          // `emptyStringToNull` turns it into a cleared field.
          ...(legacyKey ? { [legacyKey]: '' } : {})
        }
      };
    }
  };
}

/**
 * **The title reference — label bound to the jurisdiction, format validated
 * against a sibling** (`[INVDRAW-9]` / VALE-80, `[INVDRAW-3]`'s gap J).
 *
 * `getTitleReferenceLabel` is the same helper `RealEstateForm` binds to
 * (LF-4071) — never hard-code `Folio identifier`, the schema key dressed as a
 * label. The format check reads the address row's own committed `state`
 * through `[INVDRAW-3]`'s `siblings(label)` — the one place this ticket
 * exercises gap J, since the AU-state-enum check above needs no sibling at
 * all. Lenient, matching `realEstateSchema`'s `superRefine`: a missing
 * reference or an unknown state passes.
 */
function titleReferenceRow(item: InventoryItem): AttributeSpec<InventoryPatch> {
  const value = infoString(item, 'folioIdentifier');
  const ownState = infoString(item, 'state') || null;

  return {
    /*
      The label follows the property's state (`Title Reference` / `Vol/Folio` /
      `Register ID`), and that state is committed by the sibling `Property
      address` row in this same block, so the relabel happens on a live grid.
      Without a stable key the motion pass reads it as one row leaving and
      another arriving and draws both (`components/attributes/CLAUDE.md` §4b,
      VALE-934). VALE-1505.
    */
    key: 'title-reference',
    label: getTitleReferenceLabel(ownState),
    display: value || null,
    draft: value,
    editor: { kind: 'text' },
    copyable: true,
    copyText: value,
    validate: (draft, siblings) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (!raw) return null;
      const addressDraft = siblings?.('Property address');
      // Same widened-union cast as the address row above — this sibling read
      // is always the `Property address` row's own `PostalAddress` draft.
      const state =
        addressDraft && typeof addressDraft !== 'string'
          ? ((addressDraft as PostalAddress)?.state ?? null)
          : ownState;
      return validateTitleReference(raw, state);
    },
    toPatch: (draft) =>
      typeof draft === 'string' ? infoPatch('folioIdentifier', draft) : null
  };
}

/** `real-estate`'s own estate-type select — options bound to `RealEstateForm`'s
 * exported list rather than restated (root `CLAUDE.md` § "Bind to the source
 * of truth"). Nullable, unlike `Status`: an estate type can be cleared. */
function estateTypeRow(item: InventoryItem): AttributeSpec<InventoryPatch> {
  const value = infoString(item, 'estateType');
  const option = ESTATE_TYPES.find((o) => o.value === value);
  return {
    label: 'Estate type',
    display: option?.label ?? (value || null),
    draft: value,
    editor: { kind: 'select', options: ESTATE_TYPES },
    toPatch: (draft) => ({
      itemInfo: {
        estateType: typeof draft === 'string' && draft ? draft : null
      }
    })
  };
}

/**
 * **The property instruction — split, per `[INVDRAW-9]`'s acceptance.**
 * Ownership type, ownership share and contrary intention are already attribute
 * rows *inside* `PropertyInstructionSection`'s own `variant="compact"`
 * (LF-6314 / VALE-75) — ownership via `AttributeRow`/`AttributeEditableCell`,
 * contrary intention via `AttributeSegmentedBoolean`, both animated through
 * the same shared `AttributeMotionRows` this package's `CLAUDE.md` §4b
 * documents. The multi-way instruction choice and the beneficiary allocation
 * panel keep their own chrome in the same component — an allocation across
 * contacts has no attribute-row arm (gap D neighbour). So this ticket does
 * **not** decompose the component; it renders it whole as one kept
 * `component` block and wires its callbacks to `context.onCommit`.
 *
 * **The reset cascade below is a data-correctness rule ported from
 * `RealEstateForm.tsx`'s own call site**, not new behaviour: changing the
 * instruction away from `transfer-to-beneficiary` must clear the abandoned
 * transfer fields, or a "Sell" record could still carry beneficiaries from a
 * discarded choice. Under RHF this lived at the call site because each field
 * was independent local state; under commit-on-blur it has to be replicated
 * here because each call is a real write.
 */
function propertyInstructionBlock(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch> {
  const { item, contacts } = context;
  const info = (item.itemInfo ?? {}) as Record<string, unknown>;
  const commit = context.onCommit ?? (() => {});

  return {
    kind: 'component',
    title: 'INSTRUCTIONS',
    /*
      **What the AI review marks inside this block** (VALE-1597). A component
      block carries no fields for the treatment to map over, so the values it
      draws were invisible to every mark; these are the extracted fields this
      section is the only thing on the drawer that renders. See
      `AttributeComponentBlock.diffKeys`.
    */
    diffKeys: [
      'itemInfo.instruction',
      'itemInfo.ownershipType',
      'itemInfo.transferSubFlow',
      'itemInfo.transferBeneficiaries',
      'itemInfo.beneficiaryHoldingType',
      'itemInfo.hasContraryIntention',
      /* The ownership share, edited beside the ownership type (VALE-1940). */
      'itemInfo.percentageOwned'
    ],
    component: (
      <PropertyInstructionSection
        mode="edit"
        variant="compact"
        ownershipType={info.ownershipType as string | null | undefined}
        instruction={info.instruction as PropertyInstruction | null | undefined}
        transferSubFlow={
          info.transferSubFlow as PropertyTransferSubFlow | null | undefined
        }
        transferBeneficiaries={
          info.transferBeneficiaries as TransferBeneficiary[] | undefined
        }
        beneficiaryHoldingType={
          info.beneficiaryHoldingType as
            | BeneficiaryHoldingType
            | null
            | undefined
        }
        hasContraryIntention={!!info.hasContraryIntention}
        percentageOwned={info.percentageOwned as number | null | undefined}
        propertyState={info.state as string | null | undefined}
        hasLinkedMortgage={!!item.linkedInventoryItemId}
        contacts={contacts}
        /*
          **One patch for the whole ownership change** (VALE-355). The type, the
          instruction it implies, and the transfer fields that instruction abandons
          all go together — see the prop's own doc for the read-modify-write race
          that separate writes lost `ownershipType` to. `instruction === undefined`
          means the current one survives the change, so nothing about it is sent.
        */
        onOwnershipTypeChange={(type, isNonEstate, instruction) =>
          commit({
            isNonEstate,
            itemInfo: {
              ownershipType: type,
              ...(instruction !== undefined
                ? {
                    instruction,
                    ...(instruction !== 'transfer-to-beneficiary'
                      ? {
                          transferBeneficiaries: [],
                          transferSubFlow: null,
                          beneficiaryHoldingType: null,
                          hasContraryIntention: false
                        }
                      : {})
                  }
                : {})
            }
          })
        }
        onInstructionChange={(inst) =>
          commit({
            itemInfo: {
              instruction: inst,
              ...(inst !== 'transfer-to-beneficiary'
                ? {
                    transferBeneficiaries: [],
                    transferSubFlow: null,
                    beneficiaryHoldingType: null,
                    hasContraryIntention: false
                  }
                : {})
            }
          })
        }
        onTransferSubFlowChange={(sf) =>
          commit({ itemInfo: { transferSubFlow: sf } })
        }
        onTransferBeneficiariesChange={(bens) =>
          commit({ itemInfo: { transferBeneficiaries: bens } })
        }
        onBeneficiaryHoldingTypeChange={(ht) =>
          commit({ itemInfo: { beneficiaryHoldingType: ht } })
        }
        onContraryIntentionChange={(val) =>
          commit({ itemInfo: { hasContraryIntention: val } })
        }
        onPercentageOwnedChange={(val) =>
          commit({ itemInfo: { percentageOwned: val } })
        }
      />
    )
  };
}

/**
 * **`real-estate`** (`[INVDRAW-9]` / VALE-80). The address collapse is the
 * treatment's clearest win here (six controls to one row); the property
 * instruction sub-form is the other half, kept whole per
 * {@link propertyInstructionBlock}'s header note.
 */
function realEstateBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item } = context;
  const notes = infoString(item, 'notes');

  return [
    {
      kind: 'rows',
      title: 'DETAILS',
      fields: [addressRow(item), titleReferenceRow(item), estateTypeRow(item)]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        /*
          `DOD value`, the short form every other asset recipe carries
          (VALE-1479). This row spelled it in full and carried no reason for it —
          unlike `bank-account`, which at least had one until VALE-863 removed the
          heading it was about. The label column clips at roughly 104px, so the
          long form read back as `Date of death v…` beside the money figure.
        */
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    propertyInstructionBlock(context),
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          // Real estate has no institution concept, so `isInstitutionEditable`
          // is never true here — matching `RealEstateForm.tsx`'s own
          // unconditional `!isInstitutionEditable && !hideEstateReferenceField`
          // read mode gate, this row is unconditionally visible.
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * **`mortgage`** (`[INVDRAW-9]` / VALE-80) — the widest liability, already
 * split read/edit in the flag-off form. Shares the address row and the
 * signed value pair with `real-estate`; the rest
 * (`interestRate`/`repaymentAmount`/the four selects/registration fields/the
 * linked-asset row) is specific to this type.
 *
 * **Payee for Distribution is not in this ticket's own acceptance text, but
 * `MortgageFormEdit.tsx` renders it today** — dropping it on the flagged path
 * would be a regression, not a smaller scope. This shipped a simpler
 * stand-in (a single select with an implicit cleared "Estate pays") only
 * because `[INVDRAW-7]`'s shared recipe had not landed yet, and recorded
 * itself as a candidate for `[INVDRAW-7]` to reconcile. **That reconciliation
 * is done:** VALE-78 landed while this branch was open, so mortgage now uses
 * {@link payeeRows} verbatim like every other liability — the boolean-gated
 * select, `resolvePayeeDisplayName`, and the `getContactDisplayName` binding
 * that the stand-in's hand-rolled name join was silently dropping
 * (`middleName` and the `" (deceased)"` suffix).
 */
function mortgageBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false, linkedAssets } = context;
  const notes = infoString(item, 'notes');
  const interestRate = infoNumber(item, 'interestRate');
  const repaymentFrequency = infoString(item, 'repaymentFrequency');
  const encumbranceType = infoString(item, 'encumbranceType');
  const ownershipType = infoString(item, 'ownershipType');
  const resolutionPlan = infoString(item, 'resolutionPlan');
  const registrationNumber = infoString(item, 'registrationNumber');
  const registrationDate = infoString(item, 'registrationDate');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        // Joins the field flow with the other 17 (VALE-997) — it was one of the
        // two left as a `component` block, which is what made the same control
        // read differently here than on every other drawer.
        pickerRow('Lender', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Lender"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        loanAccountNumberRow(item),
        addressRow(item)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        /*
          `DOD balance`, the short form the other nine liability recipes carry
          (VALE-1479). Mortgage was the only one spelling it in full, with no
          reason recorded, and `Date of death balance` is the longest of the five
          spellings that were in use — comfortably past the ~104px the label
          column clips at.
        */
        valueRow('DOD balance', 'amountOwed', 'dod', context, {
          signed: true
        }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      kind: 'rows',
      title: 'TERMS',
      fields: [
        {
          label: 'Interest rate',
          display: interestRate == null ? null : `${interestRate}%`,
          draft: interestRate == null ? '' : String(interestRate),
          editor: { kind: 'number' },
          toPatch: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return infoPatch('interestRate', null);
            const parsed = Number(raw);
            return Number.isFinite(parsed)
              ? infoPatch('interestRate', parsed)
              : null;
          },
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = Number(raw);
            if (!Number.isFinite(parsed) || parsed < 0) {
              return 'Interest rate must be zero or more.';
            }
            return null;
          }
        },
        moneyRow('Repayment amount', 'repaymentAmount', item),
        {
          label: 'Repayment frequency',
          display:
            REPAYMENT_FREQUENCY_OPTIONS.find(
              (o) => o.value === repaymentFrequency
            )?.label ?? null,
          draft: repaymentFrequency,
          editor: { kind: 'select', options: [...REPAYMENT_FREQUENCY_OPTIONS] },
          toPatch: (draft) => ({
            itemInfo: {
              repaymentFrequency:
                typeof draft === 'string' && draft ? draft : null
            }
          })
        },
        {
          label: 'Encumbrance type',
          display:
            ENCUMBRANCE_TYPE_OPTIONS.find((o) => o.value === encumbranceType)
              ?.label ?? null,
          draft: encumbranceType,
          editor: { kind: 'select', options: [...ENCUMBRANCE_TYPE_OPTIONS] },
          toPatch: (draft) => ({
            itemInfo: {
              encumbranceType: typeof draft === 'string' && draft ? draft : null
            }
          })
        },
        {
          label: 'Ownership type',
          display:
            OWNERSHIP_TYPE_OPTIONS.find((o) => o.value === ownershipType)
              ?.label ?? null,
          draft: ownershipType,
          editor: { kind: 'select', options: [...OWNERSHIP_TYPE_OPTIONS] },
          toPatch: (draft) => ({
            itemInfo: {
              ownershipType: typeof draft === 'string' && draft ? draft : null
            }
          })
        },
        {
          label: 'Registration number',
          display: registrationNumber || null,
          draft: registrationNumber,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string'
              ? infoPatch('registrationNumber', draft)
              : null
        },
        {
          label: 'Registration date',
          display: registrationDate || null,
          draft: registrationDate,
          editor: { kind: 'date' },
          toPatch: (draft) =>
            typeof draft === 'string'
              ? infoPatch('registrationDate', draft)
              : null
        },
        {
          label: 'Resolution plan',
          display:
            RESOLUTION_PLAN_OPTIONS.find((o) => o.value === resolutionPlan)
              ?.label ?? null,
          draft: resolutionPlan,
          editor: { kind: 'select', options: [...RESOLUTION_PLAN_OPTIONS] },
          toPatch: (draft) => ({
            itemInfo: {
              resolutionPlan: typeof draft === 'string' && draft ? draft : null
            }
          })
        },
        /*
          **The shared {@link linkedAssetRow}, not a second copy of it**
          (VALE-301). This row used to be inlined here with no `None` option
          and `draft: ''` when unlinked, while `vehicle-loan` — the same
          concept, added a ticket later by `[INVDRAW-10]` — carried the
          `NONE_SENTINEL` shape. Two definitions of one thing, and the older
          one was the broken one: Radix does not fire `onValueChange` when you
          pick the value already selected, so with exactly one candidate, and
          that candidate already linked, the row committed nothing and read as
          dead. With no `None` there was also no way to unlink at any count.
          The label is the only thing that legitimately differs — the flag-off
          wording is `MortgageFormEdit.tsx`'s `label="Linked Property"`.
        */
        linkedAssetRow(item, linkedAssets, 'Linked Property')
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        liabilityDescriptionRow(item),
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];
}

/**
 * Resolve `SrnShareSearchField`'s onChange into a single patch — ported from
 * `SharesForm`'s `Controller` handler for `itemInfo.company`. Selecting a real
 * share patches ticker/company/companyName and, when the share carries its
 * own registry, the top-level `institution` + `itemInfo.shareRegistry` too
 * (a `CustomSrnShare` never does — `'institution' in share` is what tells the
 * two apart, exactly as the flag-off form does). Clearing also clears the
 * institution and the four fetched price fields, since they were computed
 * for a ticker that no longer applies.
 */
function buildShareCompanyPatch(
  share: SrnShare | CustomSrnShare | string | null
): InventoryPatch {
  if (share && typeof share === 'object') {
    const itemInfo: Record<string, unknown> = {
      company: share,
      ticker: share.ticker,
      companyName: share.companyName
    };
    const patch: InventoryPatch = { itemInfo };
    if ('institution' in share && share.institution) {
      patch.institution = { id: share.institution.id };
      itemInfo.shareRegistry = share.institution.name;
    }
    return patch;
  }
  if (typeof share === 'string') {
    return { itemInfo: { company: share } };
  }
  /*
    Cleared. Ported from `SharesForm`'s identical clear branch, including its
    one asymmetry: `itemInfo.shareRegistry` clears to `''`, but the top-level
    `institution` field is left untouched rather than sent as `null` —
    *verified live against the worktree backend*: `updateInventoryItem`
    branches on `institution !== undefined` and then unconditionally reads
    `.id` off it, so an explicit `institution: null` 500s
    (`inventory-items.service.ts`, "Cannot read properties of null (reading
    'id')") — a pre-existing backend defect this frontend-only ticket does
    not fix. `resolveInstitutionUpdateFields` (the bank-account institution
    search's path) never hits it: its return type is `{ institution?: {
    id: number } }`, never nullable.
  */
  return {
    itemInfo: {
      company: null,
      ticker: '',
      companyName: '',
      shareRegistry: '',
      sharePriceAtDateOfDeath: null,
      valueAtDateOfDeath: null,
      currentPrice: null,
      currentValue: null
    }
  };
}

/**
 * **`srn-shares` — Tier 3** (`[INVDRAW-12]` / VALE-83). One hard part stays
 * kept: `ShareInstructionSection` (the ownership/instruction choice plus its
 * beneficiary-allocation panel, exactly as `[INVDRAW-9]`'s property instruction
 * — already built with a `compact` variant for exactly this mount, LF-6314
 * follow-up). Everything genuinely label/value becomes rows.
 *
 * **The price figures are rows too, since VALE-1008.** They were the other kept
 * part — `SharePriceBlock`'s market-data card, LF-6311 gap G, a fetched and
 * stale-able value the treatment could not then express. It can now: the fetch
 * moved to the host (`share-price-sync.ts`, the same seam `institutions` uses)
 * and the figures onto the `marketFigure` editor arm. See
 * {@link sharePriceBlocks}. `SharePriceBlock` itself is untouched and still
 * renders the card for `SharesForm`, which is the AI-entity review card's
 * surface and not this one.
 *
 * **No confirmation row** — confirmed against the flag-off form: `SharesForm`
 * never touches `currentValueConfirmation`/`dodValueConfirmation`. A share's
 * value confirmation is market-driven and backend-managed (the `market` flag,
 * the `currentPriceOverridden` soft-gate), not a free-text note.
 *
 * `Non-estate asset` is a plain boolean row, same as every other type — the
 * ownership-type kept component derives and *writes* it directly (jointly
 * held ⇒ non-estate) through the same `onCommit` path, so the two never
 * disagree; it does not need to own the row as well.
 */
/**
 * **The share types' price figures, as two rows of the record** (VALE-1008).
 *
 * `srn-shares` and `hin-shares` both mount this, which is what makes "a change
 * lands on both drawers together" structural rather than something each builder
 * has to remember. It replaces the kept `SharePriceBlock` card and its
 * `Adjust values manually` disclosure: the figures a reader wants and the way to
 * change them were in two different places, several rows apart.
 *
 * ## Two rows, not four, and the price lives in the popover
 *
 * A holding's value is `price x units`, so price and value are one fact and the
 * row reads out the one a reader is looking for. Both are editable inside the
 * popover, because both were editable before it: the disclosure carried four
 * fields, and dropping the per-share price would have made a unit-price
 * correction impossible without back-solving the total.
 *
 * ## The label column is the grid's again
 *
 * These were a **label-less `component` block**, the branch of
 * `attribute-section.tsx` that publishes neither `--attr-label-w` nor the
 * `@container` it resolves against, so VALE-997 had to publish both by hand
 * inside the panel (`attributeLabelWidthStyle(1)`) to stop the four override
 * fields each starting at a different x. Real rows inherit both from the grid, so
 * that workaround is gone rather than ported. This was the ticket's "check rather
 * than assume", and it checked out.
 *
 * ## What is deliberately NOT here
 *
 * **No ticker strip.** The retired card opened with an `ANZ` badge and the
 * exchange, on a drawer whose `Ticker` row names the security two rows above, so
 * the strip restated a row that was already there. The exchange and the fetch
 * date are provenance about a *figure*, so they moved into the popover of the
 * figure they qualify, where the ticket's own framing puts them ("a popover
 * showing where the number came from"). Nothing is lost and one duplication goes.
 *
 * **No date-of-death versus today delta chip.** It compared two figures that are
 * now two rows a reader can see at once, and a percentage of a valuation is not a
 * fact the record holds.
 */
/**
 * **These two rows read as estimates when nothing vouches for them, exactly as
 * the other recipes' value rows do** (VALE-1479).
 *
 * They own no confirmation of their own — a `marketFigure` popover has no
 * document picker, because a share's provenance is the feed — but the *item*
 * carries `currentValueConfirmation` / `dodValueConfirmation` all the same: the
 * backend stamps one with its `market` flag when a fetched price is applied, and
 * the inventory list reads exactly that to decide whether to draw a share
 * holding's figure with a tick or with a `~`. Leaving these rows in full ink was
 * the only reason the drawer and the list disagreed about a share.
 *
 * This is why the whole context arrives rather than just `sharePrice`: the
 * confirmation lives on the item, and {@link figureConfirmed} is the one place
 * that reads it.
 */
function sharePriceBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  /**
   * **A surface that cannot fetch still draws the figures the record holds**
   * (VALE-1617).
   *
   * This returned `[]` without a seam, and the seam is the LIVE one — a price
   * fetch, a market payload to restore from, an in-flight flag. Only the
   * drawer and the add dialog supply it, so the AI review's detail block, which
   * has no business fetching a price mid-review, lost both `DOD value` and
   * `Current value` from every share holding: the two figures a reviewer most
   * needs to agree with, absent on the one item type that hides them, while
   * every other type showed theirs.
   *
   * The block's own rule (VALE-1363) is that "a figure is an ordinary extracted
   * field … always present when the extract carries one", so this was the share
   * recipes quietly exempting themselves from it.
   *
   * `recordedSharePriceSync` is the same derivation `useSharePriceSync` returns
   * before any fetch, asked of the item alone — not a plausible-looking stand-in
   * assembled at the call site, which would be a second definition of what a
   * share's figures are.
   */
  const sharePrice =
    context.sharePrice ??
    recordedSharePriceSync(context.item, shareScopeFor(context.typeSlug));

  const dodEstimated = isEstimatedFigure(
    sharePrice.figures.dodValue,
    figureConfirmed(context, 'dod')
  );
  const currentEstimated = isEstimatedFigure(
    sharePrice.figures.currentValue,
    figureConfirmed(context, 'current')
  );

  const {
    figures,
    market,
    isFetchingPrice,
    priceNotice,
    automaticPricingUnavailable,
    buildPatch
  } = sharePrice;
  const { numberOfShares, overridden } = figures;
  const fresh = getFreshness(figures.lastPriceUpdatedAt);

  /*
    **What these two rows declare to the AI review** (VALE-1597).

    Both figure rows compare the committed draft against the record they were
    built from and return `null` when nothing moved — correct for saving, and
    fatal for `contractKeysFor`, which asks during render where the draft IS the
    record. So they declined unconditionally, declared no keys, and the diff for
    a figure the AI had changed fell through to the review's unclaimed-key path:
    a read-only read-out of `Date of Death Balance` the reviewer could see was
    wrong and could not correct.

    Asked of `pricePatchKeyPaths` rather than typed out, because WHERE a share's
    figures live is a property of the type, not of this row. `srn-shares` keeps them flat
    under `itemInfo`; `hin-shares` and `cryptocurrency` keep them inside
    `itemInfo.portfolio`, so a hand-written `itemInfo.valueAtDateOfDeath` would
    be right for one type and name a field the other never writes. Binding to the
    same builder the commit goes through means a row cannot declare a key its own
    save does not write.

    `dodPriceSourcedAt` is deliberately NOT declared, though the real commit does
    write it: it is the internal marker saying a figure came straight from the
    market, it is not the reviewer's business, and claiming it here would light
    this row up for a change to the marker alone while the figure sat still. It
    is suppressed instead — see `UNCURATED_KEY_EXCLUSIONS`.

    The value key leads in both lists, because rule 6 states the superseded value
    from the first key carrying a change and the figure is what the row draws.
  */
  const figureScope = shareScopeFor(context.typeSlug);
  const declaredFigureKeys = (changed: Record<string, unknown>) =>
    figureScope === 'item-info'
      ? pricePatchKeyPaths(changed, figureScope)
      : /*
          **The portfolio scope declares nothing, and that is not an oversight.**

          `hin-shares` and `cryptocurrency` fold every figure into one
          `itemInfo.portfolio` array, so all three rows reading out of it — the
          share count and the two figures — resolve to the SAME key. Declaring it
          would give them one shared reject flag, which is precisely the
          container-level collision that once nulled a whole asset's information
          at submit (VALE-1181); `every-inventory-type-renders.test.tsx` fails on
          it, and it found this the first time it was tried here.

          The limit is upstream of this row anyway: the diff names the array, not
          the figure inside it, so the review cannot tell a changed date-of-death
          value from a changed share count on these types however the rows
          declare. `srn-shares` keeps its figures flat and is fixed; this scope
          needs a finer diff before it can be, and that is a backend change.
        */
        [];

  const money = (n: number | undefined) => (n == null ? '' : String(n));
  /*
    **Named as Vale's figure, not left as a bare price.** The line used to read
    `$49.50 per share`, which is unambiguous while Vale owns the figure and
    misleading the moment the reader answers `Enter manually — Yes`: an isolated
    visual review read the market price and its "updated on" date as describing
    the hand-typed number in the field below them. Saying whose price it is makes
    the popover legible in both states, and it is the same thing the retired card
    did by swapping its freshness cue for "Manually set" once overridden.
  */
  const perShare = (n: number | undefined) =>
    n == null ? null : `Vale's market price: ${formatCurrency(n)} per share`;

  /**
   * The value a raw price draft implies. `undefined` when the unit count is not
   * on file, which the editor reads as "leave the value alone" rather than as
   * "the value is now nothing" — see its `withPrice`.
   */
  const deriveValue = (price: string): number | undefined => {
    const parsed = Number(price.trim());
    if (!Number.isFinite(parsed) || numberOfShares == null) return undefined;
    return roundCents(parsed * numberOfShares);
  };

  const derivation = (price: number | undefined) =>
    price == null || numberOfShares == null
      ? null
      : `${numberOfShares.toLocaleString('en-AU')} shares at ${formatCurrency(price)} each`;

  /** A raw draft string as a figure to commit: blank clears, anything else parses. */
  const figure = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? roundCents(parsed) : null;
  };

  /*
    **The freshness cue survives as the row's badge, not as a strip.** It is a
    second, smaller fact about the value and not worth a row of its own, which is
    exactly what `badge` is for (VALE-166). Both of the card's guards come with
    it: an overridden figure says so instead, and `automaticPricingUnavailable`
    silences the cue entirely, because dating a price that no longer applies is
    what VALE-763 fixed. A delisted holding must not carry "updated 3 days ago"
    directly under a banner saying Vale cannot price it.
  */
  const currentBadge = overridden
    ? 'Manually set'
    : fresh && !automaticPricingUnavailable
      ? fresh.short
      : null;

  const currentProvenance = [
    ...(automaticPricingUnavailable
      ? []
      : [
          perShare(market?.currentPrice ?? figures.currentPrice),
          fresh?.label ?? null,
          `Listed on ${SHARE_EXCHANGE}`
        ]),
    /*
      **The fetch state belongs here, not in the badge.** It was a badge, and the
      badge renders inside the value's own target: at this column's width a
      21-character status string pushed `$24,750.00` out of its own cell and the
      figure ellipsised mid-number, to `$24,75…`. The badge slot is sized for the
      freshness cue's short form (`Today`, `3d ago`, `29 Aug 2026`), and a
      money figure is the one thing on this row that must stay readable. It is
      also the state that can stick: without an EODHD key the fetch never
      settles, so the truncation was permanent rather than momentary.
    */
    /*
      Only while there is nothing to show. Once a figure and its "updated on"
      line are both present, "Fetching the latest price" adds no information and
      reads as a contradiction beside them — and it is a line that can persist,
      since a dev environment without an EODHD key never settles the fetch.
    */
    ...(isFetchingPrice && figures.currentPrice == null
      ? ['Fetching the latest price.']
      : []),
    ...(overridden ? ['Entered by hand. Excluded from the daily refresh.'] : [])
  ].filter((line): line is string => !!line);

  const dodProvenance = [
    perShare(figures.dodPrice),
    /*
      `dodPriceSourcedAt` is the marker saying this figure is exactly what the
      market returned for that date (LF-5732); a hand-edit clears it. Saying which
      of the two a reader is looking at is the whole point of the popover.
    */
    figures.dodPrice == null
      ? null
      : sharePrice.figures.dodPrice != null && market?.dodPriceSourcedAt
        ? 'Sourced from market data for the date of death'
        : null
  ].filter((line): line is string => !!line);

  /*
    **The empty-state promise survives the card** (LF-4722, VALE-763). Before any
    figure exists the retired card said what Vale was going to do about it, and the
    two branches are not interchangeable: a delisted holding is told to enter the
    value by hand, everything else is told to wait. Dropping it would have left a
    reader looking at two `Set …` placeholders with nothing saying a price was
    coming, which is the one state where the rows say less than the card did.
  */
  const hasFigures =
    figures.dodPrice != null ||
    figures.dodValue != null ||
    figures.currentPrice != null ||
    figures.currentValue != null;

  /*
    **The promise, and only the promise.** The caption used to carry both halves
    of the retired card's empty state — what Vale is about to do, and what to do
    when it cannot. The second half is the `Current value` row's own note now
    (VALE-1008), so saying it here as well would put the same fact in two places
    one row apart, which is the restatement root `CLAUDE.md` warns about. A
    holding Vale cannot price shows the note; one it simply has not priced yet
    shows this.
  */
  const showsPromise =
    !hasFigures && !automaticPricingUnavailable && !priceNotice;

  return [
    ...(showsPromise
      ? [
          {
            kind: 'component' as const,
            title: null,
            component: (
              <p className={cn(ATTRIBUTE_LABEL_TEXT, ATTRIBUTE_MUTED_INK)}>
                Vale will value these shares automatically once the holding is
                confirmed on the market.
              </p>
            )
          }
        ]
      : []),
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          /*
            **`DOD value`** (VALE-1008), and since VALE-1479 that is simply the
            vocabulary rather than this recipe's choice: every date-of-death row
            in every recipe reads `DOD value` or `DOD balance`, the sole survivor
            of the five spellings that were in use.

            The measurement this row was decided on is what carried the rest. The
            shared label column clips at roughly 104px, and an isolated visual
            review of the long form read it back as `Date of death v…` — a
            truncated label directly beside a money figure, which is the one place
            ambiguity is least affordable. `bank-account` had a genuine reason to
            differ when this was written (`DOD value` sat under a section headed
            `DATE OF DEATH VALUE`); VALE-863 removed that heading, so it does not
            any more.
          */
          label: 'DOD value',
          display:
            figures.dodValue == null ? null : (
              <EstimatedFigure estimated={dodEstimated}>
                {formatCurrency(figures.dodValue)}
              </EstimatedFigure>
            ),
          draft: {
            value: money(figures.dodValue),
            price: money(figures.dodPrice),
            /*
              Always `false`: a date-of-death figure never resyncs, so there is
              nothing for a manual-entry answer to gate. The editor renders no
              toggle for it and its fields stay editable throughout.
            */
            manual: false
          },
          editor: {
            kind: 'marketFigure',
            valueLabel: 'DOD value',
            priceLabel: 'DOD price',
            provenance: dodProvenance,
            derivation: derivation(figures.dodPrice),
            deriveValue,
            fixedNote:
              'Date of death figures are fixed. They never refresh from the market.'
          },
          /*
            `offersCopy` is true for `marketFigure`, but the cell only falls back
            to the draft when it is a *string* — this one is an object, so
            without this the documented affordance would never render. Same
            null-guards-to-empty shape as `valueRow`'s: an absent figure copies
            nothing rather than a misleading $0.00.
          */
          copyText:
            figures.dodValue == null ? '' : formatCurrency(figures.dodValue),
          // See `declaredFigureKeys` — this row's `toPatch` declines on an
          // unmoved draft, which is every draft the review asks about.
          diffKey: declaredFigureKeys({
            valueAtDateOfDeath: null,
            sharePriceAtDateOfDeath: null
          }),
          toPatch: (draft) => {
            if (!draft || typeof draft !== 'object' || !('manual' in draft))
              return null;
            const next = draft as AttributeMarketFigure;
            const price = figure(next.price);
            const value = figure(next.value);
            const moved =
              price !== (figures.dodPrice ?? null) ||
              value !== (figures.dodValue ?? null);
            if (!moved) return null;
            return buildPatch({
              sharePriceAtDateOfDeath: price,
              valueAtDateOfDeath: value,
              /*
                A hand-edit invalidates the "this is exactly what the market
                returned" claim (LF-5732), so the marker goes. Must be `null`, not
                `undefined`: the update endpoint merges `itemInfo`, and
                `JSON.stringify` drops an `undefined` key entirely, which would
                silently leave the stale marker in place. Only sent when a figure
                actually moved, which is what the `moved` guard above is for.
              */
              dodPriceSourcedAt: null
            });
          }
        },
        {
          label: 'Current value',
          display:
            figures.currentValue == null ? null : (
              <EstimatedFigure estimated={currentEstimated}>
                {formatCurrency(figures.currentValue)}
              </EstimatedFigure>
            ),
          badge: currentBadge,
          /*
            **Why this figure needs a human, on the figure** (VALE-1008,
            carrying VALE-763's requirement to say why a price is missing).

            It was a `Banner` above the whole block: a bordered, titled object
            announcing "Price unavailable" over a section, which is louder than
            the fact deserves and does not point at the row it is about. On a
            drawer of twenty rows the reader still had to work out *which*
            figure it meant.

            Amber, not red, and {@link ATTRIBUTE_ROW_NOTE} holds that decision:
            nothing failed and nothing was refused. Vale simply cannot price this
            holding automatically, the row works exactly as intended, and the
            value is the solicitor's to enter. Red would claim a save had been
            rejected when none was attempted.
          */
          note: priceNotice,
          draft: {
            value: money(figures.currentValue),
            price: money(figures.currentPrice),
            manual: overridden
          },
          editor: {
            kind: 'marketFigure',
            valueLabel: 'Current value',
            priceLabel: 'Current price',
            provenance: currentProvenance,
            derivation: derivation(figures.currentPrice),
            deriveValue,
            manualEntry: {
              label: 'Enter manually',
              note: 'Entering this figure by hand stops it refreshing from the market.'
            }
          },
          // See the DOD row's note on why this is spelled out.
          copyText:
            figures.currentValue == null
              ? ''
              : formatCurrency(figures.currentValue),
          // See `declaredFigureKeys`. `currentPriceOverridden` is declared
          // because a reviewer taking today's figure over IS a change to this
          // row, unlike the date-of-death marker.
          diffKey: declaredFigureKeys({
            currentValue: null,
            currentPrice: null,
            currentPriceOverridden: false
          }),
          toPatch: (draft) => {
            if (!draft || typeof draft !== 'object' || !('manual' in draft))
              return null;
            const next = draft as AttributeMarketFigure;

            /*
              **Answering `No` is `resetToMarket`**, the gesture `SharePriceBlock`
              spelled as a "Reset to Vale's value" button several rows from the
              figure. Restore the fetched figures when there are any; otherwise
              clear both so the fetch-apply effect repopulates them, which is the
              branch a delisted or not-yet-priced holding takes.
            */
            if (!next.manual) {
              if (!overridden) return null;
              return buildPatch({
                currentPriceOverridden: false,
                currentPrice: market?.currentPrice ?? null,
                currentValue:
                  market?.currentValue ??
                  (market?.currentPrice != null && numberOfShares != null
                    ? roundCents(market.currentPrice * numberOfShares)
                    : null)
              });
            }

            return buildPatch({
              currentPrice: figure(next.price),
              currentValue: figure(next.value),
              currentPriceOverridden: true
            });
          }
        }
      ]
    }
  ];
}

function srnSharesBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const {
    item,
    isInstitutionEditable = false,
    contacts,
    entitlementBeneficiaryShares,
    entitlementSeedLoading,
    onCommit = () => {}
  } = context;
  /*
    `dateOfDeath` is deliberately not read here any more (VALE-1008): the
    date-of-death price fetch moved to the host, which passes it to
    `useSharePriceSync` and hands the result back as `context.sharePrice`.
  */
  const info = (item.itemInfo as Record<string, unknown> | null) ?? {};
  const srn = infoString(item, 'srn');
  const notes = infoString(item, 'notes');
  const numberOfShares =
    typeof info.numberOfShares === 'number' ? info.numberOfShares : null;
  const company = info.company as
    | SrnShare
    | CustomSrnShare
    | string
    | undefined;
  const companyValue =
    company ??
    (typeof info.ticker === 'string' && info.ticker ? info.ticker : undefined);

  /**
   * **The registry as the control can actually read it back** (VALE-1349) — the
   * same shape and the same reason as {@link linkedAssetRow}'s fallback to its
   * option list (VALE-303).
   *
   * Choosing a ticker fills the registry for the reader:
   * {@link buildShareCompanyPatch} writes `institution: { id }` — all
   * `UpdateInventoryItemRequest` carries — alongside `itemInfo.shareRegistry`,
   * the name. On the drawer that patch is a PUT and the refetched record comes
   * back with the relation hydrated, so the row reads out `Computershare`. The
   * add dialog has no round trip: `applyPatch` merges the *request* into the
   * buffer, so `item.institution` is `{ id }` with no `name` and the row sat
   * empty directly under the ticker that had just set it.
   *
   * **An object carrying the name, not the bare string it first was.**
   * `InstitutionSearchField` takes a string, but its resting read-out resolves
   * one by looking it up in the `institutions` list it was passed — so a string
   * renders only when that list happens to hold the registry, which is a
   * different bug waiting for the first drawer opened before the list loads.
   * An object goes down the field's `'id' in value` branch and needs no list.
   * (This is also why the row-level guard has to read the *resolved* read-out:
   * asserting the prop alone accepted the string and the browser did not.)
   *
   * The hydrated relation still wins whenever it has a name; this is only the
   * stand-in for the window where it does not, and `shareRegistry` is not a
   * second source of truth — it is the name written by the same commit.
   */
  const shareRegistryName = infoString(item, 'shareRegistry');
  const shareRegistryId = item.institution?.id ?? item.institutionId;
  const shareRegistryValue = item.institution?.name
    ? item.institution
    : shareRegistryName && shareRegistryId != null
      ? ({
          ...(item.institution ?? {}),
          id: shareRegistryId,
          name: shareRegistryName
        } as NonNullable<InventoryItem['institution']>)
      : item.institution;

  return [
    /*
      **One `DETAILS` section, not four** (VALE-863). This drawer opened with
      `SHARE`, then `REGISTRY`, then `VALUE`, then `DETAILS` — four headings over
      a handful of rows each, which is the "several sections" Tom's 27 Aug review
      asked to collapse. The company search, the registry, the price block and
      the notes are all *details of one asset*; the headings were dividing them
      by which subsystem owns the field rather than by anything a reader is
      looking for.

      `Instructions` below stays its own section, and that is the distinction the
      review draws: what the asset *is*, versus what is being *done* with it.
    */
    { kind: 'heading', title: 'DETAILS' },
    {
      /*
        **The two pickers are rows in the grid, not blocks of their own**
        (VALE-863). As `component` blocks they each took a whole line — so the
        section alternated one stretched field, then two, then one, and the
        single ones were as wide as the drawer for no reason. Tom's review read
        the result as unaligned even after the label columns were reconciled,
        because the *rhythm* was the problem, not the columns.

        `render` puts them in the ordinary two-column flow, so `Ticker` pairs
        with `Share Registry` and `SRN` with `Number of shares`, and each control
        is one column wide instead of eight. Both fields render `variant="cell"`
        — the control alone, since the row around it is the grid's.
      */
      kind: 'rows',
      title: null,
      fields: [
        pickerRow(
          /*
            A shareholding has exactly one institution — its registry — and it is
            the `Share Registry` row below. An `Organisation` row was added here
            while the pickers were being moved into the flow (VALE-863), bound to
            the same `item.institution` through the same `onInstitutionChange`:
            two controls over one field, so editing either silently rewrote the
            other and the drawer named one institution twice. The company is not
            an institution at all — `SrnShareSearchField` on the row below owns
            it, writing `companyName` / `ticker` / `institutionId` together.
          */
          'Ticker',
          () => (
            <SrnShareSearchField
              variant="cell"
              value={companyValue}
              onChange={(share) => onCommit(buildShareCompanyPatch(share))}
              mode="edit"
              required
              disabled={!isInstitutionEditable}
            />
          ),
          /*
            The company search, not an institution one — see `pickerRow`. It
            writes the company's name with its ticker, so the row claims both
            (VALE-1940): a changed `companyName` marks this row rather than
            having none.
          */
          ['itemInfo.ticker', 'itemInfo.companyName']
        ),
        pickerRow('Share Registry', () => (
          <InstitutionSearchField
            /*
              **Falls back to the name the ticker's own commit recorded**
              (VALE-1349), the same shape and the same reason as
              {@link linkedAssetRow}'s fallback to its option list (VALE-303).

              Choosing a ticker fills the registry for the reader:
              {@link buildShareCompanyPatch} writes `institution: { id }` —
              which is all `UpdateInventoryItemRequest` carries — alongside
              `itemInfo.shareRegistry`, the name. On the drawer that patch is a
              PUT, and the refetched record comes back with the institution
              relation hydrated, so this row reads out `Computershare`. The add
              dialog has no round trip: `applyPatch` merges the *request* into
              the buffer, so `item.institution` is `{ id }` with no `name`, and
              a control that reads out `institution.name` had nothing to show —
              the row sat empty directly under the ticker that had just set it.

              **The condition is `name`, not presence.** `item.institution` is
              not absent in the buffer — it is `{ id: 4242 }`, which is truthy,
              so a `??` fallback never fires and the row stays empty. The
              relation wins when it is *hydrated*; `shareRegistry` stands in
              whenever it is not.

              `shareRegistry` is not a second source of truth either — it is the
              name written by the same commit, and `InstitutionSearchField`
              already reads a bare string as its own display value.
            */
            value={shareRegistryValue}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Share Registry"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        ))
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          /*
            Only matters once the facility is locked in — same condition the
            flag-off form uses (`!isInstitutionEditable &&
            !hideEstateReferenceField`). Declared, never omitted, so it
            animates in the moment the facility locks.
          */
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    },
    {
      /*
        Two columns, inherited — with one **known, accepted** clip:
        "Number of shares" measures 106px against the widened ~104px column.
        Two pixels, and buying them back would cost this block the two-column
        layout VALE-294 exists to deliver, so it is carried rather than fixed.
        It is one of the 18 residual clips that ticket's `plan.md` records
        against a one-column baseline of 3.
      */
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'SRN',
          display: srn || null,
          draft: srn,
          editor: { kind: 'text' },
          // Commit `srnSchema`'s own normalised output, not the raw draft —
          // see the HIN row in `hinSharesBlocks` for the same pattern
          // (LF-6201, VALE-111).
          toPatch: (draft) => {
            if (typeof draft !== 'string') return null;
            const parsed = srnSchema.safeParse(draft.trim());
            return infoPatch(
              'srn',
              parsed.success && typeof parsed.data === 'string'
                ? parsed.data
                : draft
            );
          },
          // Bind to `srnSchema`, don't restate its regex — see the HIN row's
          // equivalent comment in `hinSharesBlocks`.
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = srnSchema.safeParse(raw);
            return parsed.success
              ? null
              : (parsed.error.issues[0]?.message ?? 'Invalid SRN');
          }
        },
        {
          label: 'Number of shares',
          display: numberOfShares == null ? null : String(numberOfShares),
          draft: numberOfShares == null ? '' : String(numberOfShares),
          editor: { kind: 'number' },
          toPatch: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return infoPatch('numberOfShares', null);
            const parsed = Number(raw);
            return Number.isFinite(parsed)
              ? infoPatch('numberOfShares', parsed)
              : null;
          },
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = Number(raw);
            return Number.isFinite(parsed) && parsed >= 0
              ? null
              : 'Number of shares must be 0 or more.';
          }
        }
      ]
    },
    ...sharePriceBlocks(context),
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          /*
            **Ownership answered here, not under `INSTRUCTIONS`** (VALE-863).
            Tom's 27 Aug review: sole-versus-joint is a fact about what the asset
            *is*; selling or transferring is a decision about what to do with it.

            The forcing rule comes from `shareOwnershipChange`, which the
            instruction editor's own handler also calls — so answering here and
            answering there write identically, rather than one of them being a
            copy that drifts.
          */
          label: 'Ownership type',
          display:
            typeof info.ownershipType === 'string' &&
            info.ownershipType &&
            info.ownershipType !== 'not-held'
              ? (SHARE_OWNERSHIP_LABELS[
                  info.ownershipType as Exclude<ShareOwnershipType, 'not-held'>
                ] ?? null)
              : null,
          draft:
            typeof info.ownershipType === 'string' ? info.ownershipType : '',
          editor: {
            kind: 'select',
            options: Object.entries(SHARE_OWNERSHIP_LABELS).map(
              ([value, label]) => ({ value, label })
            )
          },
          /*
            **Declared, for both halves of rule 2's limit** (VALE-1597).

            It declines on an unset draft, so an item whose ownership the AI is
            the first to state declared no keys at all. And when it DOES answer,
            it answers with the cascade's keys too — `shareOwnershipChange` may
            move `isNonEstate` — so the row claimed a field it does not stand
            for and would have lit up for someone else's change. One key, which
            is the one this row draws.
          */
          diffKey: 'itemInfo.ownershipType',
          toPatch: (draft) => {
            const next = typeof draft === 'string' ? draft : '';
            if (!next) return null;
            return shareFieldsPatch(
              shareOwnershipChange(
                next as ShareOwnershipType,
                (typeof info.instruction === 'string'
                  ? info.instruction
                  : null) as ShareInstruction | null
              )
            );
          }
        },
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          /*
            Kept when `Ownership type` moved up out of `INSTRUCTIONS` (VALE-863).
            This block's own doc says it: "`Non-estate asset` is a plain boolean
            row, same as every other type" — the ownership component *derives*
            and writes the flag, it does not own the row. `hin-shares`, built on
            this same recipe, keeps it too.
          */
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        }
      ]
    },
    { kind: 'heading', title: 'INSTRUCTIONS' },
    {
      kind: 'component',
      title: null,
      /*
        **What the AI review marks inside this block** (VALE-1597). A component
        block carries no fields for the treatment to map over, so the values it
        draws were invisible to every mark; these are the extracted fields this
        section is the only thing on the drawer that renders. See
        `AttributeComponentBlock.diffKeys`.
      */
      /*
        `ownershipType` is NOT declared: `ownershipAnsweredByHost` below tells
        this section not to draw it, because the `DETAILS` rows above already
        do — and that row declares the key itself.
      */
      diffKeys: ['itemInfo.instruction', 'itemInfo.transferBeneficiaries'],
      component: (
        <ShareInstructionSection
          mode="edit"
          variant="compact"
          // The `DETAILS` row above answers ownership; without this the drawer
          // would show `Ownership type` twice (VALE-863).
          ownershipAnsweredByHost
          instruction={info.instruction as ShareInstruction | null | undefined}
          ownershipType={
            info.ownershipType as ShareOwnershipType | null | undefined
          }
          isNonEstate={item.isNonEstate}
          transferBeneficiaries={
            info.transferBeneficiaries as ShareTransferBeneficiary[] | undefined
          }
          totalUnits={numberOfShares ?? undefined}
          contacts={contacts}
          entitlementBeneficiaryShares={entitlementBeneficiaryShares}
          entitlementSeedLoading={entitlementSeedLoading}
          onInstructionChange={(next) =>
            onCommit(infoPatch('instruction', next))
          }
          onOwnershipTypeChange={(next, nextIsNonEstate) =>
            onCommit({
              ...infoPatch('ownershipType', next),
              isNonEstate: nextIsNonEstate
            })
          }
          onTransferBeneficiariesChange={(next) =>
            onCommit({ itemInfo: { transferBeneficiaries: next } })
          }
          // Commit-on-blur: every callback here is its own PUT, so a change
          // touching several keys has to arrive as one (VALE-857).
          onShareFieldsChange={(changes) => onCommit(shareFieldsPatch(changes))}
          onAllocationErrorChange={() => {
            /*
              Nothing to gate under commit-on-blur — there is no Save button
              to block. The panel already surfaces an under/over-allocated
              state inline (red text + the exact unit delta), which is the
              treatment's rejected-commit contract's visual equivalent for a
              kept component.
            */
          }}
        />
      )
    }
  ];
}

/**
 * **A money row whose stored figure falls back to a *live* computed total**
 * (`cryptocurrency`'s two portfolio totals, `[INVDRAW-12]`). Distinct from
 * {@link moneyRow}: an unset total isn't blank, it's whatever the current
 * holdings sum to — matching the flag-off form's own `stored ?? sum` read.
 * Editing the row still writes an explicit, sticky figure (unchanged from
 * today), so this is a display/draft difference only, not a new write path.
 */
function cryptoTotalMoneyRow(
  label: string,
  key: string,
  item: InventoryItem,
  computedFallback: number
): AttributeSpec<InventoryPatch> {
  const stored = infoNumber(item, key);
  const effective = stored ?? computedFallback;
  return {
    label,
    display: formatCurrency(effective),
    draft: String(effective),
    editor: { kind: 'money' },
    copyText: formatCurrency(effective),
    toPatch: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (raw.length === 0) return infoPatch(key, null);
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? infoPatch(key, parsed) : null;
    },
    validate: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (raw.length === 0) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? null : `${label} must be an amount.`;
    }
  };
}

/**
 * **`cryptocurrency` — Tier 3** (`[INVDRAW-12]` / VALE-83). The repeating coin
 * list is the hard part and stays kept, whole, as a `component` block (LF-6311
 * gap E/F) — see `crypto-portfolio-kept.tsx`. Everything else genuinely
 * label/value becomes rows, following `bank-account`'s exchange/registry
 * pattern for the institution search.
 */
function cryptocurrencyBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false, onCommit = () => {} } = context;
  const info = (item.itemInfo as Record<string, unknown> | null) ?? {};
  const portfolio = (info.portfolio as CryptoHolding[] | undefined) ?? [];
  const notes = infoString(item, 'notes');
  const totalDodValue = portfolio.reduce(
    (sum, h) => sum + (h.valueAtDateOfDeath || 0),
    0
  );
  const totalCurrentValue = portfolio.reduce(
    (sum, h) => sum + (h.currentValue || 0),
    0
  );

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Cryptocurrency Exchange', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Cryptocurrency Exchange"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        /*
          The wallet the coins sit in (VALE-1940). Listed on the Grant readiness
          list, and the AI proposes it, so it has a row to correct it on.
        */
        infoTextRow(item, 'Wallet address', 'walletAddress', {
          copyable: true,
          copyText: infoString(item, 'walletAddress'),
          wraps: true
        }),
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    },
    {
      kind: 'component',
      title: null,
      /*
        **What the AI review marks inside this block** (VALE-1597). A component
        block carries no fields for the treatment to map over, so the values it
        draws were invisible to every mark; these are the extracted fields this
        section is the only thing on the drawer that renders. See
        `AttributeComponentBlock.diffKeys`.
      */
      diffKeys: ['itemInfo.portfolio'],
      component: (
        <CryptoPortfolioKept
          portfolio={portfolio}
          onChange={(next) => onCommit({ itemInfo: { portfolio: next } })}
        />
      )
    },
    {
      // Continues the PORTFOLIO section (`title: null`) — the totals are
      // conceptually part of the same holding list, just genuinely
      // label/value rather than repeating structure.
      kind: 'rows',
      title: null,
      fields: [
        cryptoTotalMoneyRow(
          'Total date of death value',
          'valueAtDateOfDeath',
          item,
          totalDodValue
        ),
        cryptoTotalMoneyRow(
          'Total current value',
          'currentValue',
          item,
          totalCurrentValue
        )
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        }
      ]
    }
  ];
}

/** The `itemInfo.portfolio[0]` keys `hin-shares` writes through a whole-array commit. */
const HIN_HOLDING_KEYS = [
  'ticker',
  'companyName',
  'numberOfShares',
  'sharePriceAtDateOfDeath',
  'valueAtDateOfDeath',
  'currentPrice',
  'currentValue',
  'currentPriceOverridden',
  'dodPriceSourcedAt'
] as const;

type HinHolding = Record<string, unknown>;

/**
 * **The holding the record's figures live on.**
 *
 * `portfolio[0]` when the array exists, else the holding a **legacy flat-shaped**
 * record implies — every pre-portfolio-array `hin-shares` row still keeps
 * `ticker` / `numberOfShares` / the price fields as top-level `itemInfo` keys,
 * and reading only `portfolio[0]` would render each of those rows blank while
 * its data sits one level up. `HINPortfolioForm`'s own read mode applies the
 * same fallback (`holding.x || itemInfo.x`).
 *
 * This is **not** VALE-123's fix: that is about `normalizeHinPortfolioData`
 * zero-seeding the same shape on the flag-off form's edit path, which this
 * builder never calls.
 */
function hinHolding(info: Record<string, unknown>): HinHolding {
  const holdings = Array.isArray(info.portfolio) ? info.portfolio : [];
  const stored = holdings[0] as HinHolding | undefined;
  if (stored) return stored;

  const legacy: HinHolding = {};
  for (const key of HIN_HOLDING_KEYS) {
    if (info[key] !== undefined) legacy[key] = info[key];
  }
  return legacy;
}

/**
 * **A patch for one `portfolio[0]` key — but it commits the whole array.**
 *
 * {@link infoPatch} writes *top-level* `itemInfo` keys, and the backend's merge
 * is one level deep (`{ ...existing, ...incoming }`), so `infoPatch('portfolio',
 * …)` with a single-key object would **replace** the array and drop every
 * sibling holding key. The array is therefore rebuilt from the cached item with
 * the one changed key folded in — the shape `CryptoPortfolioKept` already uses.
 */
function hinHoldingPatch(
  info: Record<string, unknown>,
  changed: Record<string, unknown>
): InventoryPatch {
  const holdings = Array.isArray(info.portfolio) ? info.portfolio : [];
  return {
    itemInfo: {
      portfolio: [{ ...hinHolding(info), ...changed }, ...holdings.slice(1)]
    }
  };
}

/** A `portfolio[0]` money/number row, committed through {@link hinHoldingPatch}. */
function hinHoldingNumberRow(
  label: string,
  key: string,
  info: Record<string, unknown>
): AttributeSpec<InventoryPatch> {
  const holding = hinHolding(info);
  const stored = typeof holding[key] === 'number' ? holding[key] : null;
  return {
    label,
    display: stored == null ? null : String(stored),
    draft: stored == null ? '' : String(stored),
    editor: { kind: 'number' },
    toPatch: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (raw.length === 0) return hinHoldingPatch(info, { [key]: null });
      const parsed = Number(raw);
      return Number.isFinite(parsed)
        ? hinHoldingPatch(info, { [key]: parsed })
        : null;
    },
    validate: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (raw.length === 0) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) && parsed >= 0
        ? null
        : `${label} must be 0 or more.`;
    }
  };
}

/**
 * **The ticker/company patch** — `HinShareSearchField`'s own `onChange`, as a
 * direct commit. Ported from `HINPortfolioForm`'s `itemInfo.stock` Controller,
 * including its clear branch: deselecting the stock also clears the four
 * fetched price fields, since they were computed for a ticker that no longer
 * applies.
 *
 * `itemInfo.stock` itself is **never persisted** — it is the form's own UI-only
 * holder, which `prepareInventoryItemForSubmission` strips before every save.
 * The row derives its displayed value from `portfolio[0].ticker`/`companyName`
 * instead, which is what `normalizeHinPortfolioData` reconstructs `stock` from
 * in the first place.
 */
function buildHinStockPatch(
  info: Record<string, unknown>,
  stock: TickerSearchResult | CustomHinShare | string | null
): InventoryPatch | null {
  if (stock && typeof stock === 'object') {
    return hinHoldingPatch(info, {
      ticker: stock.ticker,
      companyName: 'name' in stock ? stock.name : stock.companyName
    });
  }
  // A bare string carries no company name and `HinShareSearchField` never
  // emits one — nothing to write.
  if (typeof stock === 'string') return null;

  return hinHoldingPatch(info, {
    ticker: '',
    companyName: '',
    sharePriceAtDateOfDeath: null,
    valueAtDateOfDeath: null,
    currentPrice: null,
    currentValue: null
  });
}

/**
 * **`hin-shares` — Tier 3** (`[INVDRAW-18]` / VALE-131, reconciling
 * `[INVDRAW-13]` / VALE-84). `hin-shares` is `srn-shares` with a HIN instead of
 * an SRN and a stock broker instead of a share registry, so it follows
 * {@link srnSharesBlocks} section for section — SHARE / BROKER / VALUE /
 * DETAILS / INSTRUCTION — and keeps the same two hard parts: the fetched price
 * card and the ownership/instruction panel.
 *
 * **The one structural difference is where the figures live.** `srn-shares`
 * keeps ticker, share count and the price figures as top-level `itemInfo` keys;
 * `hin-shares` keeps them inside `itemInfo.portfolio[0]`. That is a *storage*
 * difference, not a repeating list — `HINPortfolioForm` binds `portfolio.0` as
 * its only index path (no `useFieldArray`, no add/remove, no cross-holding
 * totals), and the backend's `explode-hin-portfolio.ts` exists to keep it that
 * way by splitting a multi-stock extraction into one item per holding. So every
 * nested row commits through {@link hinHoldingPatch} rather than
 * {@link infoPatch}, and the price card takes `scope="portfolio-holding"`.
 *
 * **The HIN row's `validate` is the point of the reconciliation.** Under
 * commit-on-blur the treatment's rejected-commit contract keeps the field open
 * with the typing intact and says why — so a malformed HIN never reaches the
 * backend, where `[INVDRAW-13]`'s whole-form autosave showed the error and
 * committed anyway.
 *
 * **No confirmation row** — same verification as `srn-shares`: the flag-off form
 * never touches `currentValueConfirmation` / `dodValueConfirmation`.
 */
function hinSharesBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const {
    item,
    isInstitutionEditable = false,
    contacts,
    entitlementBeneficiaryShares,
    entitlementSeedLoading,
    onCommit = () => {}
  } = context;
  /*
    `dateOfDeath` is deliberately not read here any more (VALE-1008): the
    date-of-death price fetch moved to the host, which passes it to
    `useSharePriceSync` and hands the result back as `context.sharePrice`.
  */
  const info = (item.itemInfo as Record<string, unknown> | null) ?? {};
  const hin = infoString(item, 'hin');
  const notes = infoString(item, 'notes');
  const holding = hinHolding(info);
  const ticker = typeof holding.ticker === 'string' ? holding.ticker : '';
  const companyName =
    typeof holding.companyName === 'string' ? holding.companyName : '';
  const numberOfShares =
    typeof holding.numberOfShares === 'number' ? holding.numberOfShares : null;

  /*
    `HinShareSearchField` takes the same union its own `onChange` emits, so the
    reconstructed `CustomHinShare` shape is what `normalizeHinPortfolioData`
    already rebuilds `itemInfo.stock` from — ticker plus company name. With only
    a ticker on file the bare string is the honest value; the field renders it
    verbatim.
  */
  const stockValue: TickerSearchResult | CustomHinShare | string | undefined =
    ticker && companyName
      ? { ticker, companyName, isCustom: true }
      : ticker || undefined;

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      // Both pickers as rows in the two-column flow — see the SRN twin above
      // for why they stopped being blocks of their own.
      kind: 'rows',
      title: null,
      fields: [
        pickerRow(
          'Ticker',
          () => (
            <HinShareSearchField
              value={stockValue}
              onChange={(stock) => {
                const patch = buildHinStockPatch(info, stock);
                if (patch) onCommit(patch);
              }}
              mode="edit"
              required
              disabled={!isInstitutionEditable}
              variant="cell"
            />
          ),
          /* The company search, not an institution one — see `pickerRow`. */
          'itemInfo.ticker'
        ),
        pickerRow('Stock Broker', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Stock Broker"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        ))
      ]
    },
    {
      // Two columns, inherited — same known 106px-into-104px "Number of
      // shares" clip as `srn-shares`, carried for the same reason.
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'HIN',
          display: hin || null,
          draft: hin,
          editor: { kind: 'text' },
          // Commit `hinSchema`'s own normalised output, not the raw draft, so a
          // separator-formatted HIN is stored digits-only exactly as the
          // flag-off form stores it.
          toPatch: (draft) => {
            if (typeof draft !== 'string') return null;
            const parsed = hinSchema.safeParse(draft.trim());
            return infoPatch(
              'hin',
              parsed.success && typeof parsed.data === 'string'
                ? parsed.data
                : draft
            );
          },
          // Bind to `hinSchema`, don't restate its regex: it preprocesses with
          // `stripFormattingSeparators`, so a separator-formatted HIN the
          // flag-off form accepts must not be rejected here (root CLAUDE.md,
          // "bind to the source of truth").
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = hinSchema.safeParse(raw);
            return parsed.success
              ? null
              : (parsed.error.issues[0]?.message ?? 'Invalid HIN');
          }
        },
        hinHoldingNumberRow('Number of shares', 'numberOfShares', info)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          /*
            Only matters once the facility is locked in — the same condition the
            flag-off form uses. Declared, never omitted, so it animates in the
            moment the facility locks.
          */
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    },
    ...sharePriceBlocks(context),
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          /*
            **Ownership answered here, not under `INSTRUCTIONS`** (VALE-863).
            Tom's 27 Aug review: sole-versus-joint is a fact about what the asset
            *is*; selling or transferring is a decision about what to do with it.

            The forcing rule comes from `shareOwnershipChange`, which the
            instruction editor's own handler also calls — so answering here and
            answering there write identically, rather than one of them being a
            copy that drifts.
          */
          label: 'Ownership type',
          display:
            typeof info.ownershipType === 'string' &&
            info.ownershipType &&
            info.ownershipType !== 'not-held'
              ? (SHARE_OWNERSHIP_LABELS[
                  info.ownershipType as Exclude<ShareOwnershipType, 'not-held'>
                ] ?? null)
              : null,
          draft:
            typeof info.ownershipType === 'string' ? info.ownershipType : '',
          editor: {
            kind: 'select',
            options: Object.entries(SHARE_OWNERSHIP_LABELS).map(
              ([value, label]) => ({ value, label })
            )
          },
          /*
            **Declared, for both halves of rule 2's limit** (VALE-1597).

            It declines on an unset draft, so an item whose ownership the AI is
            the first to state declared no keys at all. And when it DOES answer,
            it answers with the cascade's keys too — `shareOwnershipChange` may
            move `isNonEstate` — so the row claimed a field it does not stand
            for and would have lit up for someone else's change. One key, which
            is the one this row draws.
          */
          diffKey: 'itemInfo.ownershipType',
          toPatch: (draft) => {
            const next = typeof draft === 'string' ? draft : '';
            if (!next) return null;
            return shareFieldsPatch(
              shareOwnershipChange(
                next as ShareOwnershipType,
                (typeof info.instruction === 'string'
                  ? info.instruction
                  : null) as ShareInstruction | null
              )
            );
          }
        },
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          label: 'Non-estate asset',
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        }
      ]
    },
    { kind: 'heading', title: 'INSTRUCTIONS' },
    {
      kind: 'component',
      title: null,
      /*
        **What the AI review marks inside this block** (VALE-1597). A component
        block carries no fields for the treatment to map over, so the values it
        draws were invisible to every mark; these are the extracted fields this
        section is the only thing on the drawer that renders. See
        `AttributeComponentBlock.diffKeys`.
      */
      /*
        `ownershipType` is NOT declared: `ownershipAnsweredByHost` below tells
        this section not to draw it, because the `DETAILS` rows above already
        do — and that row declares the key itself.
      */
      diffKeys: ['itemInfo.instruction', 'itemInfo.transferBeneficiaries'],
      component: (
        <ShareInstructionSection
          mode="edit"
          variant="compact"
          // The `DETAILS` row above answers ownership; without this the drawer
          // would show `Ownership type` twice (VALE-863).
          ownershipAnsweredByHost
          instruction={info.instruction as ShareInstruction | null | undefined}
          ownershipType={
            info.ownershipType as ShareOwnershipType | null | undefined
          }
          isNonEstate={item.isNonEstate}
          transferBeneficiaries={
            info.transferBeneficiaries as ShareTransferBeneficiary[] | undefined
          }
          totalUnits={numberOfShares ?? undefined}
          contacts={contacts}
          entitlementBeneficiaryShares={entitlementBeneficiaryShares}
          entitlementSeedLoading={entitlementSeedLoading}
          onInstructionChange={(next) =>
            onCommit(infoPatch('instruction', next))
          }
          onOwnershipTypeChange={(next, nextIsNonEstate) =>
            onCommit({
              ...infoPatch('ownershipType', next),
              isNonEstate: nextIsNonEstate
            })
          }
          onTransferBeneficiariesChange={(next) =>
            onCommit({ itemInfo: { transferBeneficiaries: next } })
          }
          // Commit-on-blur: every callback here is its own PUT, so a change
          // touching several keys has to arrive as one (VALE-857).
          onShareFieldsChange={(changes) => onCommit(shareFieldsPatch(changes))}
          onAllocationErrorChange={() => {
            /*
              Nothing to gate under commit-on-blur — there is no Save button
              to block. The panel already surfaces an under/over-allocated
              state inline (red text + the exact unit delta), which is the
              treatment's rejected-commit contract's visual equivalent for a
              kept component.
            */
          }}
        />
      )
    }
  ];
}

/**
 * The payee's read-out name — the embedded `item.payeeContact` first (no
 * extra lookup needed, and always current for a genuinely linked contact),
 * then a lookup by id against the fetched matter contacts.
 *
 * **Both branches bind to `getContactDisplayName`** (`lib/services/contacts/utils.ts`),
 * not a hand-rolled name join — that is the one owner of the `" (deceased)"`
 * suffix a predeceased beneficiary needs, and `vehicle-loan`'s own
 * `payeeRow` already binds to it for the same reason. The embedded
 * `item.payeeContact` shape carries no deceased field (pre-existing — the
 * same gap the old `PaidByField.tsx` had), so its branch never produces the
 * marker regardless — but it still goes through the one owning helper
 * rather than a second, inconsistent no-name fallback.
 */
function resolvePayeeDisplayName(
  item: InventoryItem,
  contacts: Contact[]
): string | null {
  if (item.payeeContact) {
    const name = getContactDisplayName(item.payeeContact);
    return name === 'New Contact' ? null : name;
  }
  if (item.payeeContactId != null) {
    const match = contacts.find(
      (contact) => contact.id === item.payeeContactId
    );
    return match ? getContactDisplayName(match) : null;
  }
  return null;
}

/**
 * **The payee control — settled here, [INVDRAW-8] reuses it verbatim**
 * (`plan.md`'s [INVDRAW-7] `### Actuals` — ten identical call sites are the
 * point). `PaidByField`'s two pieces — a "Paid out of pocket" checkbox
 * gating a "Payee for Distribution" contact select — map onto a `boolean`
 * row gating a `select` row via `hidden`
 * (`components/attributes/CLAUDE.md` §5), not a new capability.
 *
 * **The gate has no backing field of its own.** The persisted fact is only
 * `payeeContactId`; there is no separate "checked" column. So the box's
 * checked state is `context.pendingPaidOutOfPocket ?? (payeeContactId !=
 * null)` — the host's pending choice, for the moment the reader has ticked
 * the box but not yet chosen a contact, else derived straight off the
 * record. This is the same shape as `contact-attribute-specs.ts`'s
 * `Contact type` row (`isOrg` / `localCommit`) — the one other row in this
 * codebase gating visibility on a choice the record itself does not store.
 *
 * **Ticking on (nothing to send yet) uses `localCommit`; unticking a real
 * payee (something to send) uses `toPatch`** — the same split as that row's
 * `completesSwitchImmediately`. The `toPatch` branch also resets the
 * pending choice via the same callback, or a payee cleared this way would
 * read as still-checked until a render that disagreed with itself.
 *
 * The select offers no clear (`×`) of its own (`required: true`) —
 * un-ticking the box above is the one way to remove a payee, not a second
 * affordance on the select that would ask the same question twice.
 */
function payeeRows(
  context: InventoryBlockContext
): [AttributeSpec<InventoryPatch>, AttributeSpec<InventoryPatch>] {
  const { item, contacts = [] } = context;
  const hasPayee = item.payeeContactId != null;
  const paidOutOfPocket = context.pendingPaidOutOfPocket ?? hasPayee;

  const booleanRow: AttributeSpec<InventoryPatch> = {
    label: 'Paid out of pocket',
    display: paidOutOfPocket ? 'Yes' : 'No',
    draft: paidOutOfPocket ? 'yes' : 'no',
    editor: { kind: 'boolean' },
    required: true,
    localCommit: hasPayee
      ? undefined
      : (draft) => context.onPaidOutOfPocketChange?.(draft === 'yes'),
    /*
      **Declared, because this row's `toPatch` is not a pure question**
      (VALE-1181). It resets the host's pending choice as a side effect — the
      only hook it has for the un-tick transition, since `localCommit` replaces
      `toPatch` rather than running beside it — so a caller asking "what does
      this row write?" by calling it would fire a `setState` mid-render. The AI
      review does exactly that; see `AttributeSpec.diffKey`.
    */
    diffKey: 'payeeContactId',
    toPatch: () => {
      // Only reachable when `hasPayee` is true (`localCommit` is undefined
      // above) — the sole real transition here is checked → unchecked.
      context.onPaidOutOfPocketChange?.(false);
      return { payeeContactId: null };
    }
  };

  const contactOptions: AttributeOption[] = contacts.flatMap((contact) => {
    const label = getContactDisplayName(contact);
    return label !== 'New Contact'
      ? [{ value: String(contact.id), label }]
      : [];
  });

  const selectRow: AttributeSpec<InventoryPatch> = {
    label: 'Payee for Distribution',
    display: resolvePayeeDisplayName(item, contacts),
    draft: item.payeeContactId != null ? String(item.payeeContactId) : '',
    editor: { kind: 'select', options: contactOptions },
    hidden: !paidOutOfPocket,
    required: true,
    toPatch: (draft) => {
      const raw = typeof draft === 'string' ? draft.trim() : '';
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? { payeeContactId: parsed } : null;
    }
  };

  return [booleanRow, selectRow];
}

/**
 * **`credit-card`'s masked read-out** (gap I, LF-6311) — `****4417` at rest,
 * the raw four digits in the open editor. `AttributeSpec.display` is already
 * `React.ReactNode`, so this is a decorator on the read-out, not a new
 * editor kind.
 */
function lastFourDigitsRow(item: InventoryItem): AttributeSpec<InventoryPatch> {
  const digits = infoString(item, 'lastFourDigits');
  return {
    label: 'Last 4 digits',
    display: digits ? `****${digits}` : null,
    draft: digits,
    editor: { kind: 'text' },
    toPatch: (draft) => {
      const raw =
        typeof draft === 'string' ? draft.replace(/\D/g, '').slice(0, 4) : '';
      return infoPatch('lastFourDigits', raw);
    },
    // Strip the same way `toPatch` does before checking the pattern — a
    // separator-formatted `44-17` must not be rejected here when `toPatch`
    // would have happily normalised it to `4417` (LF-6201, VALE-111).
    validate: (draft) => {
      const raw =
        typeof draft === 'string' ? draft.trim().replace(/\D/g, '') : '';
      if (raw.length === 0) return null;
      return /^\d{4}$/.test(raw) ? null : 'Enter exactly 4 digits.';
    }
  };
}

/**
 * Notes + Estate reference — the DETAILS pair every liability builder in
 * this ticket shares. **Deliberately without `bank-account`'s "Non-estate
 * asset" row**: none of the four flag-off liability forms
 * (`FuneralBurialForm` / `LegalProfessionalForm` / `TaxLiabilityForm` /
 * `CreditCardForm`) render it — "decide ownership by rendering", not
 * copying the nearest sibling's recipe — so adding it here would be a field
 * these types never had, not a conversion of one they did.
 */
function estateDetailsFields(
  context: InventoryBlockContext
): AttributeSpec<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');
  return [
    liabilityDescriptionRow(item),
    {
      label: 'Notes',
      display: notes || null,
      draft: notes,
      editor: { kind: 'text' },
      multiline: true,
      fullWidth: true,
      clearTo: '',
      toPatch: (draft) =>
        typeof draft === 'string' ? infoPatch('notes', draft) : null
    },
    {
      /*
        Only matters once the facility is locked in — the flag-off form
        shows it under the identical condition
        (`!isInstitutionEditable && !hideEstateReferenceField`).
      */
      label: 'Estate reference',
      display: item.estateReference || null,
      draft: item.estateReference ?? '',
      editor: { kind: 'text' },
      hidden: isInstitutionEditable,
      toPatch: (draft) => ({
        estateReference: typeof draft === 'string' ? draft : ''
      })
    }
  ];
}

/**
 * **`funeral-burial` — [INVDRAW-7] / VALE-78.**
 *
 * **Corrects the plan's institution claim.** `plan.md` names institution
 * search as kept for `funeral-burial` and `legal-professional` only, and
 * separately claims "`tax-liability` has no institution field". Both
 * `taxLiabilitySchema` and `creditCardSchema` carry `institution:
 * z.any().optional()`, and their flag-off forms (`TaxLiabilityForm`,
 * `CreditCardForm`) both render a kept `InstitutionSearchField` ("Tax
 * Authority" / "Card Issuer") — confirmed against current code before
 * building, per this ticket's brief. All four builders below keep it;
 * dropping it for two of the four would be the exact downgrade the
 * rollout's integration model forbids.
 */
function funeralBurialBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const serviceDate = infoString(item, 'serviceDate');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Funeral Provider', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Funeral Provider"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Service date',
          display: serviceDate || null,
          draft: serviceDate,
          editor: { kind: 'date' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('serviceDate', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/** `legal-professional` — [INVDRAW-7] / VALE-78. */
function legalProfessionalBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const invoiceReference = infoString(item, 'invoiceReference');
  const serviceType = infoString(item, 'serviceType');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Service Provider', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Service Provider"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Invoice reference',
          display: invoiceReference || null,
          draft: invoiceReference,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string'
              ? infoPatch('invoiceReference', draft)
              : null
        },
        {
          label: 'Service type',
          display: serviceType || null,
          draft: serviceType,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('serviceType', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/** The tax types levied on a property, and so the only ones with a `Property` row. */
const PROPERTY_TAX_TYPES: ReadonlySet<string> = new Set([
  'land-tax',
  'council-rates',
  'water-rates'
]);

/** `tax-liability` — [INVDRAW-7] / VALE-78. */
function taxLiabilityBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const taxType = infoString(item, 'taxType');
  const taxPeriod = infoString(item, 'taxPeriod');
  const lodgementDate = infoString(item, 'lodgementDate');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Tax Authority', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Tax Authority"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Tax type',
          display:
            TAX_TYPE_OPTIONS.find((option) => option.value === taxType)
              ?.label ?? null,
          draft: taxType,
          editor: { kind: 'select', options: [...TAX_TYPE_OPTIONS] },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('taxType', draft) : null
        },
        {
          label: 'Tax period',
          display: taxPeriod || null,
          draft: taxPeriod,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('taxPeriod', draft) : null
        },
        {
          label: 'Lodgement date',
          display: lodgementDate || null,
          draft: lodgementDate,
          editor: { kind: 'date' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('lodgementDate', draft) : null
        },
        /*
          The property a land tax or rate is levied on (VALE-1387): it names the
          item on the handling screen, so the AI's reading must be correctable.
          Only those three taxes are about a property, so the row shows for
          them alone (VALE-1940).
        */
        infoTextRow(item, 'Property', 'propertyAddress', {
          wraps: true,
          hidden: !PROPERTY_TAX_TYPES.has(taxType)
        })
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/** `credit-card` — [INVDRAW-7] / VALE-78. */
function creditCardBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const cardType = infoString(item, 'cardType');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Card Issuer', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Card Issuer"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Card type',
          display: cardType || null,
          draft: cardType,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('cardType', draft) : null
        },
        lastFourDigitsRow(item)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/** `personal-loan` — [INVDRAW-8] / VALE-79. */
function personalLoanBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const loanType = infoString(item, 'loanType');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Lender', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Lender"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Loan type',
          display: loanType || null,
          draft: loanType,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('loanType', draft) : null
        },
        loanAccountNumberRow(item)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

const BILL_TYPE_OPTIONS = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'water', label: 'Water' },
  { value: 'internet', label: 'Internet' },
  { value: 'phone', label: 'Phone' },
  { value: 'council-rates', label: 'Council Rates' },
  { value: 'other', label: 'Other' }
] as const;

/** `utility-bill` — [INVDRAW-8] / VALE-79. */
function utilityBillBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const billType = infoString(item, 'billType');
  const accountNumber = infoString(item, 'accountNumber');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Provider', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Provider"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Bill type',
          display:
            formatSelectLabel(billType || undefined, BILL_TYPE_OPTIONS) || null,
          draft: billType,
          editor: { kind: 'select', options: [...BILL_TYPE_OPTIONS] },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('billType', draft) : null
        },
        {
          label: 'Account number',
          display: accountNumber || null,
          draft: accountNumber,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('accountNumber', draft) : null
        },
        /*
          **The same structured address `real-estate` uses** (VALE-302), not a
          second, plainer treatment of the same label. `legacyKey` carries the
          bills recorded before this row existed — see {@link addressRow}.
        */
        addressRow(item, 'propertyAddress')
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/**
 * `estate-expense` — [INVDRAW-8] / VALE-79. **The one liability with no
 * institution field at all** — `EstateExpenseForm.tsx` never renders an
 * `InstitutionSearchField` and its schema carries no institution the form
 * surfaces, so this skips the heading + kept-component pair, matching
 * `private-company`'s [INVDRAW-6] precedent. **Unlike `private-company`,
 * though**, this type's flag-off form still accepts
 * `isInstitutionEditable`/`hideEstateReferenceField` and renders "Estate
 * Reference Number" under the same gate as every other liability — confirmed
 * against the current form before assuming private-company's "no Estate
 * reference either" shape carried over here too. `estateDetailsFields` reads
 * `isInstitutionEditable` off the context directly, so no local destructure
 * is needed.
 */
function estateExpenseBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item } = context;
  const expenseType = infoString(item, 'expenseType');

  return [
    {
      kind: 'rows',
      title: 'DETAILS',
      fields: [
        {
          label: 'Expense type',
          display: expenseType || null,
          draft: expenseType,
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('expenseType', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

const CREDITOR_TYPE_OPTIONS = [
  { value: 'person', label: 'Person' },
  { value: 'entity', label: 'Entity' }
] as const;

/** `other-liability` — [INVDRAW-8] / VALE-79. */
function otherLiabilityBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, isInstitutionEditable = false } = context;
  const creditorType = infoString(item, 'creditorType');

  return [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Creditor', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Creditor"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        {
          label: 'Creditor type',
          display:
            formatSelectLabel(
              creditorType || undefined,
              CREDITOR_TYPE_OPTIONS
            ) || null,
          draft: creditorType,
          editor: { kind: 'select', options: [...CREDITOR_TYPE_OPTIONS] },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('creditorType', draft) : null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      /*
        **One column, measured — the only block that keeps the override**
        (VALE-294). At the widened 57% surface the two-column label column is
        104px, and this block owns the two longest labels in the record:
        "Paid out of pocket" (109px) and "Payee for Distribution" (128px).
        Both clip; at one column they do not. Every other block inherits the
        shared two columns.
      */
      kind: 'rows',
      title: null,
      columns: 1,
      fields: payeeRows(context)
    },
    {
      kind: 'rows',
      title: null,
      fields: estateDetailsFields(context)
    }
  ];
}

/**
 * The fields all 22 types share — what the drawer renders for a type no
 * conversion ticket has reached yet.
 *
 * Deliberately small, and each one earns its place by being genuinely universal
 * rather than by filling the panel out:
 *
 * · **`Notes`** — `itemInfo.notes`, present in 23 of the 24 type form bodies.
 *   `multiline` so the editor is a textarea rather than a line that scrolls
 *   sideways under the cursor, and **`fullWidth` because that is what makes the
 *   read-out wrap instead of truncate** — `attribute-read-out.tsx` keys
 *   `break-words whitespace-pre-line` off it, and a row without it renders
 *   `…confirmed against latest state…` (measured: dropping it truncated the
 *   fixture's own note mid-word). Its *other* effect — `sm:col-span-2` in the
 *   grid — is separately gated on `columns === 2` and is correctly inert here, so
 *   the two do not have to be reasoned about together.
 * · **`Non-estate asset`** — the top-level `isNonEstate` column. A `boolean`
 *   rather than a two-option `select`, per §1.8: *"an unticked box is
 *   indistinguishable from a question nobody answered"*.
 *
 * `Status` is not here — it is header chrome; see {@link statusRow}.
 */
function baselineBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, typeSlug } = context;
  const notes = infoString(item, 'notes');

  return [
    {
      kind: 'rows',
      title: 'DETAILS',
      /*
        Two columns, inherited. This read "one column: with two rows, one of
        them `fullWidth` prose, a second column would leave a hole beside the
        boolean" — but a `fullWidth` row spans both columns whatever the count,
        so the only row that could pair is the boolean, and it has nothing to
        pair with either way. The claim was about a hole that does not appear;
        measured clean at the widened surface.
      */
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          key: 'non-estate',
          label: nonEstateLabel(typeSlug),
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        }
      ]
    }
  ];
}

/**
 * **`vehicle`** (`[INVDRAW-10]` / VALE-81) — the simpler half of Tier 2.
 *
 * `Registration number` and `Year` are `vehicleSchema` fields
 * (`lib/schemas/inventory-form-schemas.ts`) that no inventory form has ever
 * rendered — the same "persisted but rendered by no inventory form" shape
 * `[INVDRAW-6]`'s Actuals names for `propertyType` — but this ticket's own
 * scope explicitly asks for both, so they are added here as a deliberate
 * widening rather than reproduced-as-is. `Registered state` is the third such
 * field; VALE-1940 added it as `Registered in`, since it decides which
 * transfer form a vehicle gets and the AI proposes it.
 *
 * The instruction block is the simplest of the family: chattels are
 * indivisible, so `VehicleInstructionSection` (kept as a `component`, unlike
 * real estate's [INVDRAW-9] allocation panel) offers a multi-select transfer
 * list with **no percentage split** — jointly, not proportionally.
 */
function vehicleBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const { item, typeSlug, isInstitutionEditable = false } = context;
  const notes = infoString(item, 'notes');
  const year = infoNumber(item, 'year');
  const itemInfo = (item.itemInfo ?? {}) as Record<string, unknown>;

  return [
    {
      kind: 'rows',
      title: 'DETAILS',
      fields: [
        {
          label: 'Vehicle type',
          display: infoString(item, 'vehicleType') || null,
          draft: infoString(item, 'vehicleType'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('vehicleType', draft) : null
        },
        {
          label: 'Make',
          display: infoString(item, 'make') || null,
          draft: infoString(item, 'make'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('make', draft) : null
        },
        {
          label: 'Model',
          display: infoString(item, 'model') || null,
          draft: infoString(item, 'model'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('model', draft) : null
        },
        {
          label: 'Registration number',
          display: infoString(item, 'registrationNumber') || null,
          draft: infoString(item, 'registrationNumber'),
          editor: { kind: 'text' },
          toPatch: (draft) =>
            typeof draft === 'string'
              ? infoPatch('registrationNumber', draft)
              : null
        },
        /*
          **The state of registration** (VALE-1940). It picks the transfer form
          (`redemption-form-applicability.ts`: VIC's own form is offered only for
          a VIC-registered vehicle), and the AI proposes it, so it needs a row
          that can be corrected. Always Australian, so a closed picker.
        */
        {
          label: 'Registered in',
          display:
            AUSTRALIAN_STATES.find(
              (state) => state.value === infoString(item, 'registeredState')
            )?.value ??
            (infoString(item, 'registeredState') || null),
          draft: infoString(item, 'registeredState'),
          editor: {
            kind: 'select',
            options: AUSTRALIAN_STATES.map(({ value, label }) => ({
              value,
              label
            }))
          },
          toPatch: (draft) => ({
            itemInfo: {
              registeredState: typeof draft === 'string' && draft ? draft : null
            }
          })
        },
        {
          label: 'Year',
          display: year == null ? null : String(year),
          draft: year == null ? '' : String(year),
          editor: { kind: 'number' },
          // Mirrors `vehicleSchema.itemInfo.year` (min 1900, max next year).
          validate: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return null;
            const parsed = Number(raw);
            const maxYear = new Date().getFullYear() + 1;
            if (!Number.isInteger(parsed)) return 'Enter a whole year.';
            if (parsed < 1900 || parsed > maxYear) {
              return `Enter a year between 1900 and ${maxYear}.`;
            }
            return null;
          },
          toPatch: (draft) => {
            const raw = typeof draft === 'string' ? draft.trim() : '';
            if (raw.length === 0) return infoPatch('year', null);
            const parsed = Number(raw);
            return Number.isInteger(parsed) ? infoPatch('year', parsed) : null;
          }
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD value', 'valueAtDateOfDeath', 'dod', context),
        valueRow('Current value', 'currentValue', 'current', context)
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          key: 'non-estate',
          label: nonEstateLabel(typeSlug),
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          // Matches bank-account's identical gate — the flag-off
          // `VehicleForm.tsx` shows this under the same condition despite
          // vehicle having no institution field at all.
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    },
    {
      // A kept full-width component, continuing the DETAILS section above
      // (components/attributes/CLAUDE.md §7b) — it draws its own top divider
      // and its own "Instruction" label, so it reads as a continuation
      // rather than needing a second heading.
      kind: 'component',
      title: null,
      /*
        **What the AI review marks inside this block** (VALE-1597). A component
        block carries no fields for the treatment to map over, so the values it
        draws were invisible to every mark; these are the extracted fields this
        section is the only thing on the drawer that renders. See
        `AttributeComponentBlock.diffKeys`.
      */
      diffKeys: [
        'itemInfo.instruction',
        'itemInfo.transferBeneficiaries',
        'itemInfo.hasContraryIntention'
      ],
      component: (
        <VehicleInstructionSection
          mode="edit"
          instruction={(itemInfo.instruction as VehicleInstruction) ?? null}
          transferBeneficiaries={
            itemInfo.transferBeneficiaries as
              | VehicleTransferBeneficiaryDraft[]
              | undefined
          }
          hasContraryIntention={!!itemInfo.hasContraryIntention}
          // Reproduced verbatim from the flag-off `VehicleForm.tsx` — a
          // vehicle asset's own `linkedInventoryItemId`, not derived here.
          hasLinkedLoan={!!item.linkedInventoryItemId}
          contacts={context.contacts}
          /*
            **One rule, two answers** (VALE-1363). The reset cascade — leaving
            `transfer-to-beneficiary` takes the transfer list and the contrary
            intention with it — was written out inline here, which was fine
            while this kept section was the only way to answer. The AI-extract
            review's detail block now answers it as a `select` row and calls
            `vehicleInstructionChange` too, so the two cannot drift.
          */
          onInstructionChange={(instruction) =>
            context.onCommit?.({
              itemInfo: { ...vehicleInstructionChange(instruction) }
            })
          }
          onTransferBeneficiariesChange={(beneficiaries) =>
            context.onCommit?.({
              itemInfo: { transferBeneficiaries: beneficiaries }
            })
          }
          onContraryIntentionChange={(value) =>
            context.onCommit?.({
              itemInfo: { hasContraryIntention: value }
            })
          }
        />
      )
    }
  ];
}

/** What "no one"/"nothing" means to a reference `select` that clears to a
 * sentinel rather than `''` — `Number('')` is `0`, so an empty draft would
 * post a real id. Matches the shipped `actingSolicitorField` pattern
 * (`components/matters/matter-attribute-specs.ts`)`. */
const NONE_SENTINEL = '__none__';

/**
 * **The vehicle-loan Payee row** (`[INVDRAW-10]` / VALE-81).
 *
 * The flag-off `PaidByField` is a checkbox that reveals a contact select —
 * that shape has no backing field of its own (there is nothing to persist for
 * "the box is ticked but no one is chosen yet"), which does not map onto
 * commit-on-blur's per-field write with no batch Save moment to gate on. This
 * ticket is not blocked by [INVDRAW-7] (which "settles the shape" for the
 * other nine liability types, not yet shipped), so it makes its own call:
 * **one nullable reference `select`**, exactly the shape already shipped for
 * the matter's acting-solicitor row (`NO_SOLICITOR` in
 * `components/matters/matter-attribute-specs.ts`) — "Estate pays" clears the
 * payee, any other choice sets it. Recorded here for [INVDRAW-7]/[INVDRAW-8]
 * to reconcile with (or diverge from) when they land.
 *
 * **A person is a reference, not a state** (`plainValue`, same reasoning as
 * `actingSolicitorField`), so the read-out is plain text, never the pill a
 * `select` otherwise derives.
 */
function payeeRow(
  item: InventoryItem,
  contacts: Contact[] = []
): AttributeSpec<InventoryPatch> {
  const currentId = item.payeeContactId ?? null;
  const current = contacts.find((contact) => contact.id === currentId);

  return {
    label: 'Payee for distribution',
    // The id while the contacts roster resolves, so an allocated payee never
    // reads as unallocated for a frame — same rule `actingSolicitorField`
    // follows.
    display: current
      ? getContactDisplayName(current)
      : currentId != null
        ? `#${currentId}`
        : null,
    draft: currentId != null ? String(currentId) : NONE_SENTINEL,
    editor: {
      kind: 'select',
      options: [
        { value: NONE_SENTINEL, label: 'Estate pays' },
        ...contacts.map((contact) => ({
          value: String(contact.id),
          label: getContactDisplayName(contact)
        }))
      ]
    },
    plainValue: true,
    clearTo: NONE_SENTINEL,
    toPatch: (draft) => {
      if (typeof draft !== 'string') return null;
      if (draft === NONE_SENTINEL) return { payeeContactId: null };
      const parsed = Number(draft);
      return Number.isInteger(parsed) ? { payeeContactId: parsed } : null;
    }
  };
}

/**
 * **The Linked Vehicle row** (`[INVDRAW-10]` / VALE-81) — `LinkedAssetField`'s
 * equivalent as a reference `select` over sibling `vehicle`-type items,
 * filtered by the drawer from the already-loaded inventory cache.
 *
 * The read-out format (`description ($value)`) is bound to exactly what
 * `LinkedAssetField.tsx` already produces, not restated as a new format.
 */
function linkedAssetRow(
  item: InventoryItem,
  linkedAssets: Array<{
    id: number;
    description: string;
    value: number | null;
  }> = [],
  label = 'Linked vehicle'
): AttributeSpec<InventoryPatch> {
  const currentId = item.linkedInventoryItemId ?? null;

  /*
    **The read-out falls back to the option list** (VALE-303).

    `display` used to read `item.linkedInventoryItem` alone — the *hydrated
    relation*. A commit writes `linkedInventoryItemId` and the cache takes the
    new id, but nothing re-hydrates the relation, so the row fell straight back
    to its `Set …` placeholder the moment you picked something: the select held
    the right value while the read-out claimed the field was empty. The same
    gap shows at rest on any payload that carries the id without the join.

    `linkedAssets` is the very list the options are built from and already
    holds `description` and `value` for that id, so resolving from it needs no
    extra fetch and cannot disagree with what the reader just chose. The
    relation still wins when present — it is the authority; this is only the
    stand-in for the window where it is missing.
  */
  const linked =
    item.linkedInventoryItem ??
    (currentId != null
      ? linkedAssets.find((asset) => asset.id === currentId)
      : undefined);

  return {
    label,
    display: linked
      ? linked.value != null
        ? `${linked.description} (${formatCurrency(linked.value)})`
        : linked.description
      : null,
    draft: currentId != null ? String(currentId) : NONE_SENTINEL,
    editor: {
      kind: 'select',
      options: [
        { value: NONE_SENTINEL, label: 'None' },
        ...linkedAssets.map((asset) => ({
          value: String(asset.id),
          label:
            asset.value != null
              ? `${asset.description} (${formatCurrency(asset.value)})`
              : asset.description
        }))
      ]
    },
    // A vehicle is a reference, not a state.
    plainValue: true,
    clearTo: NONE_SENTINEL,
    toPatch: (draft) => {
      if (typeof draft !== 'string') return null;
      if (draft === NONE_SENTINEL) return { linkedInventoryItemId: null };
      const parsed = Number(draft);
      return Number.isInteger(parsed)
        ? { linkedInventoryItemId: parsed }
        : null;
    }
  };
}

/** Mirrors `VehicleLoanForm.tsx`'s local (not exported) options array. */
const VEHICLE_LOAN_RESOLUTION_PLAN_OPTIONS = [
  { value: 'vehicle-sale', label: 'Vehicle Sale' },
  { value: 'beneficiary-assumes', label: 'Beneficiary Assumes' },
  { value: 'estate-pays', label: 'Estate Pays' },
  { value: 'undecided', label: 'Undecided' }
] as const;

/**
 * **`vehicle-loan`** (`[INVDRAW-10]` / VALE-81) — the other half of Tier 2.
 *
 * `Resolution plan` is never editable on this surface, even in the flag-off
 * form (`VehicleLoanForm.tsx` renders it `mode="read"` in both its own read
 * *and* edit branches, with a note that it is set elsewhere) — it is derived
 * from the linked vehicle's own instruction, not a field this record owns. A
 * `component` block reproduces that exact read-only look; there is no row
 * for it, because there is nothing here to commit.
 */
function vehicleLoanBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const {
    item,
    typeSlug,
    isInstitutionEditable = false,
    contacts = [],
    linkedAssets = []
  } = context;
  const notes = infoString(item, 'notes');
  const resolutionPlan = infoString(item, 'resolutionPlan');

  const blocks: (AttributeBlock<InventoryPatch> | null)[] = [
    { kind: 'heading', title: 'DETAILS' },
    {
      kind: 'rows',
      title: null,
      fields: [
        pickerRow('Lender', () => (
          <InstitutionSearchField
            value={item.institution}
            onChange={context.onInstitutionChange ?? (() => {})}
            onInstitutionSearch={context.onInstitutionSearch}
            institutions={context.institutions ?? []}
            loadingInstitutions={context.loadingInstitutions ?? false}
            mode="edit"
            institutionTypeName="Lender"
            disabled={!isInstitutionEditable}
            hideLabel
            variant="compact"
          />
        )),
        loanAccountNumberRow(item),
        {
          label: 'Ownership type',
          display:
            formatSelectLabel(
              infoString(item, 'ownershipType') || undefined,
              OWNERSHIP_TYPE_OPTIONS
            ) || null,
          draft: infoString(item, 'ownershipType'),
          editor: {
            kind: 'select',
            options: OWNERSHIP_TYPE_OPTIONS.map(({ value, label }) => ({
              value,
              label
            }))
          },
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('ownershipType', draft) : null
        },
        linkedAssetRow(item, linkedAssets)
      ]
    },
    /*
      **A row, not a component block** (VALE-1597).

      It was a `component` block because it is never editable here, and a
      read-only `FormField` reproduced the flag-off form's look exactly. But a
      component block carries no fields, so the AI-review treatment could not
      reach it: an extracted resolution plan drew no ink, no badge, no popover
      and offered no reject — the one value in this block a reviewer might want
      to turn down.

      Unlike the four instruction sections, this one genuinely IS a label and a
      value with a note under it, which is what a row already is. So it is
      expressed as one rather than declaring `diffKeys` for a layout the grid can
      draw itself, and it is covered by construction from here on. `hidden` is
      the row-native way to condition it, which also restores the before/after
      the grid needs — §7b's leave-it-out-of-the-array rule is a workaround for
      a component block having no `hidden`, not a preference.
    */
    {
      kind: 'rows' as const,
      title: null,
      fields: [
        {
          label: 'Resolution plan',
          display: resolutionPlan
            ? (formatSelectLabel(
                resolutionPlan,
                VEHICLE_LOAN_RESOLUTION_PLAN_OPTIONS
              ) ?? resolutionPlan)
            : null,
          note: "Set on the linked vehicle's instructions sheet.",
          draft: resolutionPlan,
          /*
            A `select` although it never opens (VALE-1940): the row declares
            the four plans the value can be, so it agrees with the backend's set
            instead of reading as free text.
          */
          editor: {
            kind: 'select' as const,
            options: VEHICLE_LOAN_RESOLUTION_PLAN_OPTIONS.map(
              ({ value, label }) => ({ value, label })
            )
          },
          /*
            Derived from the linked vehicle's own instruction, never set here —
            `VehicleLoanForm.tsx` renders it `mode="read"` in both its read and
            edit branches for the same reason.
          */
          readOnly: true,
          hidden: !resolutionPlan,
          /*
            Read-only rows commit nothing, so `contractKeysFor` would walk an
            empty patch and the treatment would have no key to match the diff
            against. Declared, exactly as a kept picker declares its own.
          */
          diffKey: 'itemInfo.resolutionPlan',
          toPatch: () => null
        }
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        valueRow('DOD balance', 'amountOwed', 'dod', context, { signed: true }),
        valueRow('Current balance', 'currentBalance', 'current', context, {
          signed: true
        })
      ]
    },
    {
      kind: 'rows',
      title: null,
      fields: [payeeRow(item, contacts)]
    },
    {
      kind: 'rows',
      title: null,
      fields: [
        liabilityDescriptionRow(item),
        {
          label: 'Notes',
          display: notes || null,
          draft: notes,
          editor: { kind: 'text' },
          multiline: true,
          fullWidth: true,
          clearTo: '',
          toPatch: (draft) =>
            typeof draft === 'string' ? infoPatch('notes', draft) : null
        },
        {
          key: 'non-estate',
          label: nonEstateLabel(typeSlug),
          display: item.isNonEstate ? 'Yes' : 'No',
          draft: item.isNonEstate ? 'yes' : 'no',
          editor: { kind: 'boolean' },
          required: true,
          toPatch: (draft) =>
            draft === 'yes' || draft === 'no'
              ? { isNonEstate: draft === 'yes' }
              : null
        },
        {
          label: 'Estate reference',
          display: item.estateReference || null,
          draft: item.estateReference ?? '',
          editor: { kind: 'text' },
          hidden: isInstitutionEditable,
          toPatch: (draft) => ({
            estateReference: typeof draft === 'string' ? draft : ''
          })
        }
      ]
    }
  ];

  return blocks.filter(
    (block): block is AttributeBlock<InventoryPatch> => block !== null
  );
}

/**
 * Per-type block builders, keyed by type slug — `bank-account` is the first,
 * `[INVDRAW-6…13]` add the remaining 21.
 *
 * A slug with no entry renders {@link baselineBlocks}. That fallback is what
 * makes the rollout safely incremental: the flag can be on for a firm while only
 * some types are converted, and an unconverted type shows a correct — if thin —
 * record rather than an empty drawer.
 */
export const INVENTORY_BLOCK_BUILDERS: Record<
  string,
  (context: InventoryBlockContext) => AttributeBlock<InventoryPatch>[]
> = {
  'bank-account': bankAccountBlocks,
  superannuation: superannuationBlocks,
  'life-insurance': lifeInsuranceBlocks,
  'managed-funds': managedFundsBlocks,
  'private-company': privateCompanyBlocks,
  other: otherBlocks,
  'employment-entitlements': employmentEntitlementsBlocks,
  'nursing-home-bond': nursingHomeBondBlocks,
  'real-estate': realEstateBlocks,
  mortgage: mortgageBlocks,
  vehicle: vehicleBlocks,
  'vehicle-loan': vehicleLoanBlocks,
  'srn-shares': srnSharesBlocks,
  cryptocurrency: cryptocurrencyBlocks,
  'hin-shares': hinSharesBlocks,
  'funeral-burial': funeralBurialBlocks,
  'legal-professional': legalProfessionalBlocks,
  'tax-liability': taxLiabilityBlocks,
  'credit-card': creditCardBlocks,
  'personal-loan': personalLoanBlocks,
  'utility-bill': utilityBillBlocks,
  'estate-expense': estateExpenseBlocks,
  'other-liability': otherLiabilityBlocks
};

/**
 * **A diff key's value, read off the item** — the read-side twin of
 * `contractKeysFor`'s write-side walk (VALE-1181).
 *
 * Dot-notation, because that is what the inventory diff speaks: `itemInfo.bsb`
 * for an `info`-backed field, a bare `institution` for the one relation it
 * diffs. It lived in `compact-inventory-detail.tsx` until VALE-1617 and moved
 * here with the derivation below, so the drawer and the review's detail block
 * read a picker's value the same way.
 */
function pickerValueAtPath(item: InventoryItem, key: string): unknown {
  if (key === 'institution') return item.institution?.name ?? null;
  let cursor: unknown = item;
  for (const part of key.split('.')) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

/**
 * **A kept picker declares the value its control draws** (VALE-1617).
 *
 * {@link pickerRow} builds `display: null`, because the control owns the value
 * slot and nothing in the grid prints a picker's `display`. But the AI-review
 * treatment reads `display` for a different question — *does this row hold a
 * value at all* (`rowHoldsAValue`) — and a `null` there answers no. Rule 5a
 * then drops the row before rule 5b can ink it, so on a CREATED item, where the
 * diff carries no `fieldChanges` to fall back on, the row that names the bank
 * was the one row on the surface that could not say the AI had supplied it.
 *
 * The review's detail block had already worked this out and derived the value
 * itself; the drawer had not, so the two disagreed about one row. Deriving it
 * once here is what stops a third surface having to rediscover it — the same
 * move `confirmationProvenanceTip` and `estimate-figure` make for their facts.
 *
 * **`null` when there is nothing recorded, never the placeholder.** The
 * treatment reads this to decide whether the row holds a value, and
 * `describeChangeValue` answers with the app's not-recorded mark for an empty
 * one — which is right when something is being PRINTED and wrong here: a `'—'`
 * says yes, and an item with no institution then drew `Bank / Set bank…` inked
 * as NEW with a reject beside it, crediting the AI with a value it never
 * supplied. That is the defect rule 5a exists to stop, arriving from the other
 * side.
 *
 * `rendersValueAsText` returns false for any row with a `render`, whatever its
 * `display`, so declaring one cannot reroute a picker down the text branch —
 * that guard was written for exactly this.
 */
function declarePickerValues(
  blocks: AttributeBlock<InventoryPatch>[],
  item: InventoryItem
): AttributeBlock<InventoryPatch>[] {
  return blocks.map((block) => {
    if (block.kind !== 'rows') return block;
    return {
      ...block,
      fields: block.fields.map((field) => {
        const diffKey = (field as InventoryPickerSpec).diffKey;
        if (!field.render || !diffKey || field.display != null) return field;
        const drawn = drawnPickerKey(diffKey);
        const raw = pickerValueAtPath(item, drawn);
        return {
          ...field,
          display:
            raw == null || raw === ''
              ? null
              : describeChangeValue(raw, { key: drawn })
        };
      })
    };
  });
}

export function inventoryBlocks(
  context: InventoryBlockContext
): AttributeBlock<InventoryPatch>[] {
  const builder = INVENTORY_BLOCK_BUILDERS[context.typeSlug];
  const blocks = builder ? builder(context) : baselineBlocks(context);
  return declarePickerValues(
    claimVerbatimInstitutionNames(
      withoutInstructionBlocks(blocks, context.runsLifecycle ?? true),
      context.typeSlug
    ),
    context.item
  );
}

/**
 * **The institution row claims the name the document wrote** (VALE-1940).
 *
 * A `machine` key (`lenderName`, `fundName`, …) is the institution's name
 * verbatim, saved with the item and never a row of its own: the institution
 * row represents it. So that row declares those keys after `institution`, and a
 * change the AI makes to the verbatim name marks the row that names the
 * institution rather than rendering as a second, uneditable one.
 */
function claimVerbatimInstitutionNames(
  blocks: AttributeBlock<InventoryPatch>[],
  typeSlug: string
): AttributeBlock<InventoryPatch>[] {
  const verbatim = VERBATIM_INSTITUTION_NAME_FIELDS[typeSlug];
  if (!verbatim?.length) return blocks;
  return blocks.map((block) => {
    if (block.kind !== 'rows') return block;
    return {
      ...block,
      fields: block.fields.map((field) =>
        (field as InventoryPickerSpec).diffKey === 'institution'
          ? {
              ...field,
              diffKey: [
                'institution',
                ...verbatim.map((key) => `itemInfo.${key}`)
              ]
            }
          : field
      )
    };
  });
}

/**
 * **Drop the disposition-instruction block on a register-only matter type**
 * (VALE-1764).
 *
 * *Sell · Transfer to a beneficiary · Keep* are questions about realising a
 * **deceased** person's asset. The backend refuses every one of them on a matter
 * that keeps a register rather than running the lifecycle
 * (`assertInstructionWorkflowAllowedOnMatterType`), so a planning matter's
 * drawer would be offering a four-way choice whose every answer 400s.
 *
 * **Derived from the block's own `diffKeys`, not from its title.** All four
 * instruction blocks — property, the two share types, vehicle — already declare
 * `itemInfo.instruction` there for the AI review's marks, and nothing else does.
 * Matching on a title string would be a second definition of which block this
 * is, and the vehicle one has no title at all: it continues the DETAILS section
 * and draws its own label. The `INSTRUCTIONS` heading above a dropped block goes
 * with it, or the drawer keeps a heading over nothing.
 */
function withoutInstructionBlocks(
  blocks: AttributeBlock<InventoryPatch>[],
  runsLifecycle: boolean
): AttributeBlock<InventoryPatch>[] {
  if (runsLifecycle) return blocks;

  const isInstruction = (block: AttributeBlock<InventoryPatch>) =>
    block.kind === 'component' &&
    (block.diffKeys ?? []).includes('itemInfo.instruction');

  return blocks.filter((block, index) => {
    if (isInstruction(block)) return false;
    const next = blocks[index + 1];
    const orphanHeading =
      block.kind === 'heading' &&
      block.title === 'INSTRUCTIONS' &&
      next !== undefined &&
      isInstruction(next);
    return !orphanHeading;
  });
}

/**
 * **The row the drawer would open for one of an item's two figures** (VALE-1032)
 * — for a surface that wants that one field and not the whole record.
 *
 * The inventory list's Current View is the caller. It used to ask the same
 * question through `QuickSetValueModal`, which had to restate the drawer's
 * answers to three things — which `itemInfo` key the figure is written under,
 * what the field is called, and whether a negative is legal — and one of those
 * restatements is exactly what LF-5599 shipped wrong (`currentValue` for a
 * bank account whose drawer reads `currentBalance`, so the list showed a value
 * the drawer said was empty). Resolving the row through the same builders the
 * drawer renders means there is nothing left to restate: whatever the drawer
 * would open is what the list opens.
 *
 * **It runs the type's whole builder** rather than a cheaper lookup, because the
 * per-type key lives in the builder's own `valueRow` call and nowhere else — a
 * table mapping slug to key would be the second definition this exists to
 * delete. Builders are pure and the elements a `component` block constructs are
 * never rendered, so the cost is one object graph.
 *
 * `null` when the type has no row for that figure — the caller renders its
 * static read-out instead of an affordance that would open nothing.
 */
export function inventoryValueSpec(
  context: InventoryBlockContext,
  valueKind: 'current' | 'dod'
): InventoryValueSpec | null {
  for (const block of inventoryBlocks(context)) {
    if (block.kind !== 'rows') continue;
    for (const field of block.fields) {
      if (
        field.editor.kind === 'valueWithConfirmation' &&
        (field as InventoryValueSpec).valueKind === valueKind
      ) {
        return field as InventoryValueSpec;
      }
    }
  }
  return null;
}
